  import React, { useState, useEffect, useCallback, useRef } from "react";
  import { useNavigate } from "react-router-dom";
  import { useAuth } from "./AuthContext";
  import { useBot } from "./BotContext";
  import ThemeCustomizer from "./ThemeCustomizer";
  import API_BASE_URL, { WIDGET_BASE_URL } from "./config";
  import { authenticatedFetch } from "./api";
  import { MAX_UPLOAD_MB, getUploadSizeError, getUploadErrorMessage } from "./uploadLimits";
  import {
    parseKeywordList,
    findKeywordOverlaps,
    collectRedirectKeywords,
    formatKeywordConflictMessage,
  } from "./keywordUtils";
  import "./AdminPanel.css";

  // Question Form Component
  const QuestionForm = ({ question, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      questionText: question?.questionText || '',
      questionType: question?.questionType || 'text_single',
      options: question?.options || [],
      
      isRequired: true,
      orderIndex: question?.orderIndex || 0
    });

    const handleInputChange = (field, value) => {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    };

    const handleOptionChange = (index, value) => {
      const newOptions = [...formData.options];
      newOptions[index] = value;
      setFormData(prev => ({
        ...prev,
        options: newOptions
      }));
    };

    const addOption = () => {
      setFormData(prev => ({
        ...prev,
        options: [...prev.options, '']
      }));
    };

    const removeOption = (index) => {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        options: newOptions
      }));
    };

    const handleSubmit = (e) => {
      e.preventDefault();

      const submitData = {
        questionText: formData.questionText.trim(),
        questionType: formData.questionType,
        isRequired: formData.isRequired,
        orderIndex: parseInt(formData.orderIndex)
      };

      // Add options for choice-based questions
      if (formData.questionType === 'single_choice' || formData.questionType === 'multiple_choice') {
        submitData.options = formData.options.filter(option => option.trim() !== '');
        if (submitData.options.length === 0) {
          alert('Please add at least one option for choice questions');
          return;
        }
      }

      onSubmit(submitData);
    };

    return (
      <div className="question-form-overlay">
        <div className="question-form">
          <h3>{question ? 'Edit Question' : 'Add New Question'}</h3>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Question Text *</label>
              <textarea
                value={formData.questionText || ''}
                onChange={(e) => handleInputChange('questionText', e.target.value)}
                placeholder="Enter your question here..."
                required
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Question Type *</label>
              <select
                value={formData.questionType || 'text_single'}
                onChange={(e) => handleInputChange('questionType', e.target.value)}
              >
                <option value="text_single">Single Line Text</option>
                <option value="text_multiline">Multi Line Text</option>
                <option value="single_choice">Single Choice</option>
                <option value="multiple_choice">Multiple Choice</option>
                <option value="rating">Rating (1-5)</option>
              </select>
            </div>

            {(formData.questionType === 'single_choice' || formData.questionType === 'multiple_choice') && (
              <div className="form-group">
                <label>Options *</label>
                {formData.options.map((option, index) => (
                  <div key={index} className="option-input">
                    <input
                      type="text"
                      value={option || ''}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                    />
                    <button type="button" onClick={() => removeOption(index)}>
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addOption} className="add-option-btn">
                  Add Option
                </button>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={formData.orderIndex || 0}
                  onChange={(e) => handleInputChange('orderIndex', e.target.value)}
                  min="0"
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="save-btn">
                {question ? 'Update' : 'Create'} Question
              </button>
              <button type="button" onClick={onCancel} className="cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Redirect Question Form Component
  const RedirectForm = ({ question, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      questionText: question?.question_text || '',
      keywords: question?.keywords?.join(', ') || '',
      isActive: question?.is_active !== undefined ? question.is_active : true
    });

    // Update form data when question prop changes
    useEffect(() => {
      setFormData({
        questionText: question?.question_text || '',
        keywords: question?.keywords?.join(', ') || '',
        isActive: question?.is_active !== undefined ? question.is_active : true
      });
    }, [question]);

    const handleSubmit = (e) => {
      e.preventDefault();
      const keywordsArray = formData.keywords.split(',').map(k => k.trim()).filter(k => k !== '');
      onSubmit({
        question_text: formData.questionText.trim(),
        keywords: keywordsArray,
        is_active: formData.isActive
      });
    };

    return (
      <div className="question-form-overlay">
        <div className="question-form">
          <h3>{question ? 'Edit Redirect Question' : 'Add New Redirect Question'}</h3>
          <p className="form-help-text" style={{ fontSize: '12px', color: '#64748b', marginBottom: '15px' }}>
            Questions matching these patterns will redirect users to a high-priority support ticket flow.
          </p>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Sample Question *</label>
              <textarea
                value={formData.questionText || ''}
                onChange={(e) => setFormData({ ...formData, questionText: e.target.value })}
                placeholder="e.g., Can you tell me about the confidential project budget?"
                required
                rows="3"
              />
              <small style={{ color: '#94a3b8' }}>Semantic similarity will be used to match variations of this question.</small>
            </div>

            <div className="form-group">
              <label>Keywords (Comma separated)</label>
              <input
                type="text"
                value={formData.keywords || ''}
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                placeholder="budget, confidential, salary, internal"
              />
              <small style={{ color: '#94a3b8' }}>Direct keyword matches will also trigger redirection. Must not overlap with Popup Configuration keywords.</small>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                Is Active
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" className="save-btn">
                {question ? 'Update' : 'Create'} Trigger
              </button>
              <button type="button" onClick={onCancel} className="cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Default bot settings - used for initial state and when clearing on bot change
  const DEFAULT_BOT_SETTINGS = {
    bot_name: 'AI Customer Support',
    welcome_message: 'Hello! I am your AI Assistant. To help me serve you better, I would love to learn more about you first.',
    response_delay_seconds: 1.0,
    survey_enabled: true,
    window_size: 'medium',
    sound_enabled: true,
    theme_text_color: '#2d3748',
    theme_bot_bubble_bg: '#ffffff',
    theme_user_bubble_bg: '#f8fafc',
    theme_wallpaper_url: '',
    theme_wallpaper_mode: 'cover',
    popup_enabled: false,
    popup_type: 'message',
    popup_message: '',
    popup_link_text: '',
    popup_link_url: '',
    popup_frequency: 'once_per_session',
    popup_frequency_minutes: 0,
    popup_start_delay_seconds: 0,
    popup_dismissible: true,
    popup_trigger_words: '',
    chat_icon: '💬',
    chat_icon_enabled: true,
  };

  const AdminPanel = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated, logout, setAdminContext, getAuthToken } = useAuth();
    const { selectedBotId, setSelectedBotId, bots, setBots, isLoadingBots, setIsLoadingBots } = useBot();
    
    // Verify user is org admin - if not, log them out and redirect to login
    useEffect(() => {
      if (user?.role && user.role !== 'org_admin') {
        logout();
        navigate('/login', { replace: true });
      }
    }, [user, logout, navigate]);
    
    const [activeTab, setActiveTab] = useState("dashboard");
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [uploadingFiles, setUploadingFiles] = useState([]); // Track files being uploaded
    const [uploadPage, setUploadPage] = useState(1);
    const [uploadPageSize, setUploadPageSize] = useState(10);
    const [uploadTotal, setUploadTotal] = useState(0);
    const [selectedFileIds, setSelectedFileIds] = useState(new Set());

    // Knowledge base external imports (v1 UI - minimal)
    const [kbDbForm, setKbDbForm] = useState({
      name: "External DB",
      db_type: "postgres",
      host: "",
      port: "5432",
      database: "",
      username: "",
      password: "",
      schema: null,
    });
    const [kbDbConnectionId, setKbDbConnectionId] = useState(null);
    const [kbDbTables, setKbDbTables] = useState([]);
    const [kbDbTablesPage, setKbDbTablesPage] = useState(1);
    const [kbDbTablesPageSize, setKbDbTablesPageSize] = useState(20);
    const [kbDbTablesTotal, setKbDbTablesTotal] = useState(0);
    const [kbDbSelectedTables, setKbDbSelectedTables] = useState([]);
    const [kbDbTableSearchQuery, setKbDbTableSearchQuery] = useState("");
    const [kbDbMode, setKbDbMode] = useState("entire_table"); // entire_table | choose_columns
    const [kbDbColumnsText, setKbDbColumnsText] = useState(""); // comma-separated
    const [kbDbMaxRows, setKbDbMaxRows] = useState(1000);
    const [kbDbJobId, setKbDbJobId] = useState(null);
    const [kbDbJobStatus, setKbDbJobStatus] = useState(null);
    const [kbDbLoading, setKbDbLoading] = useState(false);
    const [kbDbError, setKbDbError] = useState("");

    const [kbOsForm, setKbOsForm] = useState({
      name: "Object Storage",
      provider: "s3",
      bucket: "",
      prefix: "",
      access_key_id: "",
      secret_access_key: "",
      session_token: "",
      region: "",
      service_account_json: "",
      connection_string: "",
      account_url: "",
      account_key: "",
      sas_token: "",
    });
    const [kbOsConnectionId, setKbOsConnectionId] = useState(null);
    const [kbOsJobId, setKbOsJobId] = useState(null);
    const [kbOsLoading, setKbOsLoading] = useState(false);
    const [kbOsObjectKeysText, setKbOsObjectKeysText] = useState(""); // comma-separated
    const [kbOsMaxObjects, setKbOsMaxObjects] = useState(50);

    const [kbCrawlForm, setKbCrawlForm] = useState({
      seed_url: "",
      respect_robots: true,
      max_pages: 50,
      max_depth: 2,
      schedule_enabled: true,
      schedule_interval_unit: "daily",
      schedule_interval_value: 1,
      schedule_weekday: 1,
      schedule_time_utc: "00:00",
    });
    const [kbCrawlLoading, setKbCrawlLoading] = useState(false);
    const [kbCrawlRunId, setKbCrawlRunId] = useState(null);
    const [kbCrawlRunStatus, setKbCrawlRunStatus] = useState(null);
    const [kbCrawlStatusError, setKbCrawlStatusError] = useState("");
    const [kbCrawlRefreshing, setKbCrawlRefreshing] = useState(false);
    const [kbCrawlStatusNotice, setKbCrawlStatusNotice] = useState("");
    const [kbSourceType, setKbSourceType] = useState("file_upload"); // file_upload | database | web_crawl | object_storage
    const [viewingFile, setViewingFile] = useState(null);
    const [viewFileContent, setViewFileContent] = useState("");
    const [viewFileLoading, setViewFileLoading] = useState(false);
    const [viewFileError, setViewFileError] = useState("");
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);

    // Reset pagination when switching bots or changing page size.
    useEffect(() => {
      setUploadPage(1);
    }, [selectedBotId, uploadPageSize]);
    const [conversationFilters, setConversationFilters] = useState({
      status: 'all',
      name: '',
      requestId: '',
      dateFrom: '',
      dateTo: '',
      dateRange: ''
    });
    const [showCustomRangeModal, setShowCustomRangeModal] = useState(false);
    const [customRangeDates, setCustomRangeDates] = useState({
      from: '',
      to: ''
    });
    const [predefinedQuestions, setPredefinedQuestions] = useState([]);
    const [showQuestionForm, setShowQuestionForm] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [repetitiveQuestions, setRepetitiveQuestions] = useState([]);
    const [showRepetitiveForm, setShowRepetitiveForm] = useState(false);
    const [editingRepetitiveQuestion, setEditingRepetitiveQuestion] = useState(null);
    const [botSettings, setBotSettings] = useState({ ...DEFAULT_BOT_SETTINGS });
    const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);
    const [dashboardStats, setDashboardStats] = useState({
      total_conversations: 0,
      active_conversations: 0,
      pending_conversations: 0,
      resolved_percentage: 0,
      total_messages: 0,
      avg_messages_per_conversation: 0,
      total_files: 0,
      total_file_size: 0,
      active_admins: 0,
      active_questions: 0,
      recent_conversations: 0
    });
    const [isLoadingStats, setIsLoadingStats] = useState(false);
    const [dashboardViewMode, setDashboardViewMode] = useState("kpi"); // kpi | charts
    const dashboardStatsTimeoutRef = useRef(null);
    // bots, selectedBotId, isLoadingBots are now managed by BotContext
    const [showBotForm, setShowBotForm] = useState(false);
    const [organizationInfo, setOrganizationInfo] = useState(null);
    const [editingBot, setEditingBot] = useState(null);
    const [redirectQuestions, setRedirectQuestions] = useState([]);
    const [showRedirectForm, setShowRedirectForm] = useState(false);
    const [editingRedirectQuestion, setEditingRedirectQuestion] = useState(null);
    const [escalatedTickets, setEscalatedTickets] = useState([]);
    const [isLoadingTickets, setIsLoadingTickets] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);

    const normalizeDashboardStats = useCallback((rawStats) => {
      const stats = rawStats || {};
      const totalConversations = Number(stats.total_conversations || 0);
      const totalMessages = Number(stats.total_messages || 0);
      const backendAverage = Number(stats.avg_messages_per_conversation || 0);
      const computedAverage = totalConversations > 0 ? Number((totalMessages / totalConversations).toFixed(2)) : 0;

      return {
        total_conversations: totalConversations,
        active_conversations: Number(stats.active_conversations || 0),
        pending_conversations: Number(stats.pending_conversations || 0),
        resolved_percentage: Number(stats.resolved_percentage || 0),
        total_messages: totalMessages,
        // Prefer backend value when valid; otherwise derive from live totals.
        avg_messages_per_conversation: backendAverage > 0 ? backendAverage : computedAverage,
        total_files: Number(stats.total_files || 0),
        total_file_size: Number(stats.total_file_size || 0),
        active_admins: Number(stats.active_admins || 0),
        active_questions: Number(stats.active_questions || 0),
        recent_conversations: Number(stats.recent_conversations || 0),
      };
    }, []);

    // Ensure user is properly formatted
    const userInfo = user && typeof user === 'object' ? user : { username: 'Admin' };

    const handleLogout = () => {
      logout();
    };

    // Helper functions for file operations
    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const normalized = String(dateString).trim();
      const date = new Date(normalized);
      if (isNaN(date.getTime())) return '-';
      // API sends UTC (…Z); browser converts to the user's local timezone
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      });
    };

    const fetchKbCrawlRunStatus = useCallback(async (runId) => {
      if (!runId) {
        throw new Error('No crawl run to refresh. Start a crawl first.');
      }
      const statusResp = await authenticatedFetch(
        `/api/knowledge/webcrawl/runs/${encodeURIComponent(runId)}`
      );
      if (!statusResp.ok) {
        const err = await statusResp.json().catch(() => ({}));
        throw new Error(err.message || err.error || `Failed to fetch crawl status (${statusResp.status}).`);
      }
      const statusJson = await statusResp.json();
      const run = statusJson.run || null;
      if (!run) {
        throw new Error('Crawl run not found.');
      }
      setKbCrawlRunStatus(run);
      setKbCrawlRunId(run.id || runId);
      if (run.status === 'failed') {
        setKbCrawlStatusError(run.errorMessage || 'Crawl failed.');
      } else {
        setKbCrawlStatusError('');
      }
      return run;
    }, []);

    // Conversation functions - require selected bot
    const fetchConversations = useCallback(async () => {
      if (!selectedBotId) {
        setConversations([]);
        return;
      }
      try {
        const response = await authenticatedFetch(`/api/conversations?bot_id=${encodeURIComponent(selectedBotId)}`);
        if (response.ok) {
          const result = await response.json();
          setConversations(result.conversations);
        } else {
          setConversations([]);
        }
      } catch (error) {
        console.error('Error fetching conversations:', error);
        setConversations([]);
      }
    }, [selectedBotId]);


    const fetchRedirectQuestions = useCallback(async () => {
      if (!selectedBotId) {
        setRedirectQuestions([]);
        return;
      }
      try {
        const response = await authenticatedFetch(`/api/redirect-questions?bot_id=${encodeURIComponent(selectedBotId)}`);
        if (response.ok) {
          const result = await response.json();
          setRedirectQuestions(result.questions || []);
        } else {
          setRedirectQuestions([]);
        }
      } catch (error) {
        console.error('Error fetching redirect questions:', error);
        setRedirectQuestions([]);
      }
    }, [selectedBotId]);

    const fetchEscalatedTickets = useCallback(async () => {
      if (!selectedBotId) {
        setEscalatedTickets([]);
        return;
      }
      setIsLoadingTickets(true);
      try {
        const response = await authenticatedFetch(`/api/tickets?bot_id=${encodeURIComponent(selectedBotId)}`);
        if (response.ok) {
          const result = await response.json();
          setEscalatedTickets(result.tickets || []);
        } else {
          setEscalatedTickets([]);
        }
      } catch (error) {
        console.error('Error fetching escalated tickets:', error);
        setEscalatedTickets([]);
      } finally {
        setIsLoadingTickets(false);
      }
    }, [selectedBotId]);

    const fetchConversationDetails = useCallback(async (conversationId) => {
      try {
        const response = await authenticatedFetch(`/api/conversations/${conversationId}`);
        if (response.ok) {
          const result = await response.json();
          setSelectedConversation(result.conversation);
        } else {
          console.error('Failed to fetch conversation details');
        }
      } catch (error) {
        console.error('Error fetching conversation details:', error);
      }
    }, []);

    const updateConversationStatus = async (conversationId, status) => {
      try {
        const response = await authenticatedFetch(`/api/conversations/${conversationId}/status`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status }),
        });

        if (response.ok) {
          alert(`Conversation status updated to: ${status}`);
          fetchConversations(); // Refresh conversations list
        } else {
          alert('Failed to update conversation status');
        }
      } catch (error) {
        console.error('Error updating conversation status:', error);
        alert('Failed to update conversation status');
      }
    };

    const updateTicketStatus = async (ticketId, status) => {
      try {
        const response = await authenticatedFetch(`/api/tickets/${ticketId}/status`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status }),
        });

        if (response.ok) {
          alert(`Ticket status updated to: ${status}`);
          fetchEscalatedTickets(); // Refresh tickets list
        } else {
          alert('Failed to update ticket status');
        }
      } catch (error) {
        console.error('Error updating ticket status:', error);
        alert('Failed to update ticket status');
      }
    };

    // Pre-defined questions functions
    const fetchPredefinedQuestions = useCallback(async () => {
      if (!selectedBotId) {
        setPredefinedQuestions([]);
        return;
      }
      try {
        const response = await authenticatedFetch(`/api/predefined-questions?bot_id=${encodeURIComponent(selectedBotId)}`);
        if (response.ok) {
          const result = await response.json();
          setPredefinedQuestions(result.questions);
        } else {
          console.error('Failed to fetch predefined questions');
        }
      } catch (error) {
        console.error('Error fetching predefined questions:', error);
      }
    }, [selectedBotId]);

    // Bot Settings functions
    const fetchBotSettings = useCallback(async (botId = null) => {
      if (!botId || !String(botId).trim()) {
        setBotSettings({ ...DEFAULT_BOT_SETTINGS });
        return;
      }
      if (!getAuthToken()) {
        return; // Avoid 401: only fetch when we have a token
      }
      try {
        const response = await authenticatedFetch(`/api/settings?bot_id=${encodeURIComponent(botId)}`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.settings) {
            setBotSettings(prev => ({
              ...prev,
              ...result.settings,
              bot_id: botId || result.settings.bot_id || null
            }));
          }
        } else {
          setBotSettings({ ...DEFAULT_BOT_SETTINGS });
        }
      } catch (error) {
        console.error('Error fetching bot settings:', error);
        setBotSettings({ ...DEFAULT_BOT_SETTINGS });
      }
    }, [authenticatedFetch]);

    // When selected bot changes: clear all bot-scoped state, then fetch settings for new bot (only when authenticated to avoid 401)
    useEffect(() => {
      setBotSettings({ ...DEFAULT_BOT_SETTINGS });
      setUploadedFiles([]);
      setSelectedFileIds(new Set());
      setConversations([]);
      setRedirectQuestions([]);
      setPredefinedQuestions([]);
      setRepetitiveQuestions([]);
      setEscalatedTickets([]);
      if (isAuthenticated && selectedBotId) {
        fetchBotSettings(selectedBotId);
      }
    }, [isAuthenticated, selectedBotId]); // eslint-disable-line react-hooks/exhaustive-deps -- only run when bot or auth changes; fetchBotSettings is stable

    const updateBotSettings = async (newSettings) => {
      if (!selectedBotId) {
        alert('Please select a bot first. Settings are unique per bot.');
        return;
      }

      const popupKeywords = parseKeywordList(newSettings.popup_trigger_words);
      if (newSettings.popup_enabled && popupKeywords.length) {
        const redirectKw = collectRedirectKeywords(redirectQuestions);
        const overlaps = findKeywordOverlaps(popupKeywords, redirectKw);
        if (overlaps.length) {
          alert(formatKeywordConflictMessage(overlaps, 'Redirect Questions'));
          return;
        }
      }

      setIsUpdatingSettings(true);
      try {
        const settingsToSave = {
          ...newSettings,
          bot_id: selectedBotId
        };
        
        const response = await authenticatedFetch(`/api/settings`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(settingsToSave),
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            // Merge with defaults to ensure all properties are defined
            setBotSettings(prev => ({
              ...prev,
              ...result.settings
            }));
            alert('ChatBot settings updated successfully!');
          } else {
            alert('Failed to update settings: ' + (result.message || 'Unknown error'));
          }
        } else {
          const errorData = await response.json();
          alert('Failed to update settings: ' + (errorData.message || 'Server error'));
        }
      } catch (error) {
        console.error('Error updating bot settings:', error);
        alert('Network error. Please try again.');
      } finally {
        setIsUpdatingSettings(false);
      }
    };

    const handleSettingsChange = (field, value) => {
      setBotSettings(prev => ({
        ...prev,
        [field]: value
      }));
    };

    const handleSaveSettings = async () => {
      await updateBotSettings(botSettings);
    };

    // Dashboard statistics functions - require selected bot
    const fetchDashboardStats = useCallback(async () => {
      if (!selectedBotId) {
        setDashboardStats({
          total_conversations: 0,
          active_conversations: 0,
          pending_conversations: 0,
          resolved_percentage: 0,
          total_messages: 0,
          avg_messages_per_conversation: 0,
          total_files: 0,
          total_file_size: 0,
          active_admins: 0,
          active_questions: 0,
          recent_conversations: 0
        });
        return;
      }
      setIsLoadingStats(true);
      try {
        const response = await authenticatedFetch(`/api/dashboard/stats?bot_id=${encodeURIComponent(selectedBotId)}`);
        if (response.ok) {
          const result = await response.json();
          console.log('API Response:', JSON.stringify(result, null, 2));

          // Accept stats even if success is false (error handler returns stats with success: false)
          if (result.stats) {
            console.log('Setting dashboard stats:', result.stats);
            setDashboardStats(normalizeDashboardStats(result.stats));
          } else {
            console.error('Invalid response format:', result);
            setDashboardStats(normalizeDashboardStats({}));
          }
        } else {
          console.error('Failed to fetch dashboard stats, status:', response.status);
          const errorText = await response.text();
          console.error('Error response:', errorText);
          setDashboardStats(normalizeDashboardStats({}));
        }
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        setDashboardStats(normalizeDashboardStats({}));
      } finally {
        setIsLoadingStats(false);
      }
    }, [normalizeDashboardStats, selectedBotId]);

    // Conversation filter functions
    const handleFilterChange = (filterType, value) => {
      setConversationFilters(prev => ({
        ...prev,
        [filterType]: value
      }));
    };

    const clearFilters = () => {
      setConversationFilters({
        status: 'all',
        name: '',
        requestId: '',
        dateFrom: '',
        dateTo: '',
        dateRange: ''
      });
    };

    // Date filter helper functions
    const setDateRange = (range) => {
      if (range === 'custom') {
        setShowCustomRangeModal(true);
        return;
      }

      const today = new Date();
      let fromDate = '';
      let toDate = '';

      switch (range) {
        case 'today':
          fromDate = toDate = today.toISOString().split('T')[0];
          break;
        case 'yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(today.getDate() - 1);
          fromDate = toDate = yesterday.toISOString().split('T')[0];
          break;
        case 'last7days':
          const lastWeek = new Date(today);
          lastWeek.setDate(today.getDate() - 7);
          fromDate = lastWeek.toISOString().split('T')[0];
          toDate = today.toISOString().split('T')[0];
          break;
        case 'last30days':
          const lastMonth = new Date(today);
          lastMonth.setDate(today.getDate() - 30);
          fromDate = lastMonth.toISOString().split('T')[0];
          toDate = today.toISOString().split('T')[0];
          break;
        case 'thisMonth':
          const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
          fromDate = firstDay.toISOString().split('T')[0];
          toDate = today.toISOString().split('T')[0];
          break;
        default:
          return;
      }

      setConversationFilters(prev => ({
        ...prev,
        dateFrom: fromDate,
        dateTo: toDate,
        dateRange: range
      }));
    };

    // Custom range functions
    const handleCustomRangeSubmit = () => {
      if (customRangeDates.from && customRangeDates.to) {
        setConversationFilters(prev => ({
          ...prev,
          dateFrom: customRangeDates.from,
          dateTo: customRangeDates.to
        }));
        setShowCustomRangeModal(false);
      }
    };

    const handleCustomRangeCancel = () => {
      setShowCustomRangeModal(false);
      setCustomRangeDates({ from: '', to: '' });
    };

    const getFilteredConversations = () => {
      return conversations.filter(conversation => {
        // Status filter
        if (conversationFilters.status !== 'all' && conversation.status !== conversationFilters.status) {
          return false;
        }

        // Name filter (case-insensitive)
        if (conversationFilters.name && !conversation.userName.toLowerCase().includes(conversationFilters.name.toLowerCase())) {
          return false;
        }

        // Request ID filter
        if (conversationFilters.requestId && conversation.request_id) {
          const requestIdStr = conversation.request_id.toString();
          if (!requestIdStr.includes(conversationFilters.requestId)) {
            return false;
          }
        }

        // Date filter (use created/start time primarily; fallback to last message time)
        if (conversationFilters.dateFrom || conversationFilters.dateTo) {
          const baseTime = conversation.startTime || conversation.lastMessageTime;
          const conversationDate = new Date(baseTime);

          if (conversationFilters.dateFrom) {
            const fromDate = new Date(conversationFilters.dateFrom);
            fromDate.setHours(0, 0, 0, 0); // Start of day
            if (conversationDate < fromDate) {
              return false;
            }
          }

          if (conversationFilters.dateTo) {
            const toDate = new Date(conversationFilters.dateTo);
            toDate.setHours(23, 59, 59, 999); // End of day
            if (conversationDate > toDate) {
              return false;
            }
          }
        }

        return true;
      });
    };

    const getStatusVariant = (status = '') => {
      const normalized = status.toLowerCase();
      switch (normalized) {
        case 'active':
          return 'status-in-progress';
        case 'pending':
          return 'status-warning';
        case 'resolved':
          return 'status-success';
        case 'in-progress':
          return 'status-primary';
        case 'closed':
          return 'status-neutral';
        default:
          return 'status-neutral';
      }
    };

    const getPriorityLevel = (conversation) => {
      const count = conversation?.messageCount || 0;
      const status = (conversation?.status || '').toLowerCase();

      if (status === 'pending' || status === 'in-progress') {
        return 'high';
      }

      if (count >= 15) {
        return 'high';
      }

      if (count >= 8) {
        return 'medium';
      }

      return 'low';
    };

    const formatRelativeTime = (timestamp) => {
      if (!timestamp) return 'No activity';
      const now = new Date();
      const target = new Date(timestamp);

      if (isNaN(target.getTime())) {
        return 'Unknown';
      }

      const diff = now - target;
      const seconds = Math.floor(diff / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (seconds < 60) return 'Just now';
      if (minutes < 60) return `${minutes}m ago`;
      if (hours < 24) return `${hours}h ago`;
      if (days < 7) return `${days}d ago`;

      return target.toLocaleDateString();
    };

    useEffect(() => {
      if (activeTab !== 'conversations') return;

      if (conversations.length === 0) {
        setSelectedConversation(null);
        return;
      }

      const currentIds = new Set(conversations.map((c) => c.id));
      if (!selectedConversation || !currentIds.has(selectedConversation.id)) {
        const firstConversation = conversations[0];
        if (firstConversation) {
          fetchConversationDetails(firstConversation.id);
        }
      }
    }, [activeTab, conversations, selectedConversation, fetchConversationDetails]);

    // Repetitive questions functions
    const fetchRepetitiveQuestions = useCallback(async () => {
      if (!selectedBotId) {
        setRepetitiveQuestions([]);
        return;
      }
      try {
        const response = await authenticatedFetch(`/api/repetitive-questions?bot_id=${encodeURIComponent(selectedBotId)}`);
        if (response.ok) {
          const result = await response.json();
          console.log('[AdminPanel] Fetched repetitive questions:', result.questions?.length || 0, 'questions');
          setRepetitiveQuestions(result.questions || []);
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('Failed to fetch repetitive questions:', response.status, errorData);
        }
      } catch (error) {
        console.error('Error fetching repetitive questions:', error);
      }
    }, [selectedBotId]);

    useEffect(() => {
      if (activeTab === 'repetitive') {
        fetchRepetitiveQuestions();
      }
    }, [activeTab, fetchRepetitiveQuestions, selectedBotId]);

    const createPredefinedQuestion = async (questionData) => {
      if (!selectedBotId) {
        alert('Please select a Bot ID before creating questions.');
        return false;
      }
      try {
        // Include bot_id in the request
        const requestData = {
          ...questionData,
          bot_id: selectedBotId
        };
        const response = await authenticatedFetch(`/api/predefined-questions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });

        if (response.ok) {
          const result = await response.json();
          alert('Question created successfully!');
          setShowQuestionForm(false);
          setEditingQuestion(null);
          fetchPredefinedQuestions(); // Refresh questions list
          return true;
        } else {
          const error = await response.json();
          alert(`Failed to create question: ${error.message}`);
          return false;
        }
      } catch (error) {
        console.error('Error creating predefined question:', error);
        alert('Failed to create question');
        return false;
      }
    };

    const updatePredefinedQuestion = async (questionId, questionData) => {
      if (!selectedBotId) {
        alert('Please select a Bot ID before updating questions.');
        return false;
      }
      try {
        // Include bot_id in the request
        const requestData = {
          ...questionData,
          bot_id: selectedBotId
        };
        const response = await authenticatedFetch(`/api/predefined-questions/${questionId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });

        if (response.ok) {
          alert('Question updated successfully!');
          setShowQuestionForm(false);
          setEditingQuestion(null);
          fetchPredefinedQuestions(); // Refresh questions list
          return true;
        } else {
          const error = await response.json();
          alert(`Failed to update question: ${error.message}`);
          return false;
        }
      } catch (error) {
        console.error('Error updating predefined question:', error);
        alert('Failed to update question');
        return false;
      }
    };

    // Wrapper function for editing that takes only questionData
    const handleEditQuestion = async (questionData) => {
      if (!editingQuestion) return false;
      console.log('Frontend: Editing question with data:', questionData);
      console.log('Frontend: Question ID:', editingQuestion.id);
      return await updatePredefinedQuestion(editingQuestion.id, questionData);
    };

    const deletePredefinedQuestion = async (questionId) => {
      if (!window.confirm('Are you sure you want to delete this question?')) {
        return;
      }

      try {
        const response = await authenticatedFetch(`/api/predefined-questions/${questionId}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          alert('Question deleted successfully!');
          fetchPredefinedQuestions(); // Refresh questions list
        } else {
          alert('Failed to delete question');
        }
      } catch (error) {
        console.error('Error deleting predefined question:', error);
        alert('Failed to delete question');
      }
    };

    const createRepetitiveQuestion = async (questionData) => {
      if (!selectedBotId) {
        alert('Please select a Bot ID before creating questions.');
        return;
      }
      try {
        // Include bot_id in the request
        const requestData = {
          ...questionData,
          bot_id: selectedBotId
        };
        console.log('[AdminPanel] Creating repetitive question:', requestData);
        const response = await authenticatedFetch(`/api/repetitive-questions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });

        if (response.ok) {
          const result = await response.json();
          console.log('[AdminPanel] Repetitive question created successfully:', result);
          alert('Repetitive question created successfully!');
          setShowRepetitiveForm(false);
          setEditingRepetitiveQuestion(null);
          // Refresh the list
          await fetchRepetitiveQuestions();
        } else {
          const error = await response.json();
          console.error('[AdminPanel] Failed to create repetitive question:', response.status, error);
          alert(`Failed to create repetitive question: ${error.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error creating repetitive question:', error);
        alert('Failed to create repetitive question. Please try again.');
      }
    };

    const handleEditRepetitiveQuestion = async (questionData) => {
      if (!selectedBotId) {
        alert('Please select a Bot ID before updating questions.');
        return;
      }
      try {
        // Include bot_id in the request
        const requestData = {
          ...questionData,
          bot_id: selectedBotId
        };
        const response = await authenticatedFetch(`/api/repetitive-questions/${editingRepetitiveQuestion.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });

        if (response.ok) {
          alert('Repetitive question updated successfully!');
          setShowRepetitiveForm(false);
          setEditingRepetitiveQuestion(null);
          fetchRepetitiveQuestions();
        } else {
          const error = await response.json();
          alert(`Failed to update repetitive question: ${error.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error updating repetitive question:', error);
        alert('Failed to update repetitive question. Please try again.');
      }
    };

    const deleteRepetitiveQuestion = async (questionId) => {
      if (!window.confirm('Are you sure you want to delete this repetitive question?')) {
        return;
      }

      try {
        const response = await authenticatedFetch(`/api/repetitive-questions/${questionId}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          alert('Repetitive question deleted successfully!');
          fetchRepetitiveQuestions();
        } else {
          alert('Failed to delete repetitive question. Please try again.');
        }
      } catch (error) {
        console.error('Error deleting repetitive question:', error);
        alert('Failed to delete repetitive question. Please try again.');
      }
    };

    // File upload functions - require bot (uses selectedBotId when not passed)
    const fetchUploadedFiles = useCallback(async (botId = null, page = null, pageSize = null) => {
      const effectiveBotId = botId ?? selectedBotId;
      if (!effectiveBotId) {
        setUploadedFiles([]);
        return;
      }
      const effectivePage = page ?? uploadPage;
      const effectivePageSize = pageSize ?? uploadPageSize;
      try {
        const response = await authenticatedFetch(
          `/api/uploads/files?bot_id=${encodeURIComponent(effectiveBotId)}&page=${effectivePage}&pageSize=${effectivePageSize}`
        );
        if (response.ok) {
          const result = await response.json();
          setUploadedFiles(result.files);
          setUploadTotal(result.total || 0);
        } else {
          setUploadedFiles([]);
          setUploadTotal(0);
        }
      } catch (error) {
        console.error('Error fetching files:', error);
        setUploadedFiles([]);
        setUploadTotal(0);
      }
    }, [selectedBotId, uploadPage, uploadPageSize]);

    const handleRefreshCrawlStatus = useCallback(async () => {
      const runId = kbCrawlRunStatus?.id || kbCrawlRunId;
      setKbCrawlRefreshing(true);
      setKbCrawlStatusNotice('');
      try {
        const run = await fetchKbCrawlRunStatus(runId);
        if (run?.status === 'completed') {
          await fetchUploadedFiles(selectedBotId);
          setKbCrawlStatusNotice('Crawl completed. Files library updated.');
        } else if (run?.status === 'running') {
          setKbCrawlStatusNotice('Crawl is still running. Status will update automatically.');
        } else if (run?.status === 'failed') {
          setKbCrawlStatusNotice('');
        } else {
          setKbCrawlStatusNotice('Status refreshed.');
        }
      } catch (err) {
        setKbCrawlStatusError(err.message || 'Failed to fetch crawl status.');
        setKbCrawlStatusNotice('');
      } finally {
        setKbCrawlRefreshing(false);
      }
    }, [kbCrawlRunId, kbCrawlRunStatus?.id, selectedBotId, fetchKbCrawlRunStatus, fetchUploadedFiles]);

    useEffect(() => {
      if (kbSourceType !== 'web_crawl' || !selectedBotId) return undefined;
      let cancelled = false;
      (async () => {
        try {
          const resp = await authenticatedFetch(
            `/api/knowledge/webcrawl/runs/latest?bot_id=${encodeURIComponent(selectedBotId)}`
          );
          if (!resp.ok || cancelled) return;
          const data = await resp.json();
          if (data?.run && !cancelled) {
            setKbCrawlRunStatus(data.run);
            setKbCrawlRunId(data.run.id);
            if (data.run.status !== 'failed') {
              setKbCrawlStatusError('');
            }
          }
        } catch (_) {
          /* ignore */
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [kbSourceType, selectedBotId]);

    useEffect(() => {
      if (kbSourceType !== 'web_crawl') return undefined;
      const runId = kbCrawlRunStatus?.id || kbCrawlRunId;
      if (!runId || kbCrawlRunStatus?.status !== 'running') return undefined;

      const tick = async () => {
        try {
          const run = await fetchKbCrawlRunStatus(runId);
          if (run?.status === 'completed') {
            await fetchUploadedFiles(selectedBotId);
            setKbCrawlStatusNotice('Crawl completed. Files library updated.');
          }
        } catch (_) {
          /* keep polling */
        }
      };

      const interval = setInterval(tick, 2500);
      return () => clearInterval(interval);
    }, [
      kbSourceType,
      kbCrawlRunId,
      kbCrawlRunStatus?.id,
      kbCrawlRunStatus?.status,
      selectedBotId,
      fetchKbCrawlRunStatus,
      fetchUploadedFiles,
    ]);

    const uploadFile = useCallback(async (file, botId = null) => {
      // Require botId for upload
      if (!botId) {
        alert('Please select a Bot ID before uploading files. Each file must be associated with a specific bot.');
        return false;
      }

      const sizeError = getUploadSizeError(file.size);
      if (sizeError) {
        alert(sizeError);
        return false;
      }
      
      const fileId = `${file.name}-${Date.now()}`; // Unique ID for this upload
      const fileInfo = {
        id: fileId,
        name: file.name,
        size: file.size,
        status: 'uploading' // 'uploading', 'success', 'error'
      };

      // Add to uploading files list
      setUploadingFiles(prev => [...prev, fileInfo]);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('uploadedBy', userInfo.username || 'admin');
      formData.append('bot_id', botId); // Required - always include bot_id

      try {
        const token = getAuthToken();
        // Don't set Content-Type header - browser will set it automatically for FormData with boundary
        const response = await authenticatedFetch(`/api/uploads/upload`, {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          const result = await response.json();
          console.log('File uploaded successfully:', result);
          
          if (result.warning) {
            alert(result.warning);
          }
          
          const nextStatus = result.processing ? 'processing' : 'success';
          setUploadingFiles(prev => prev.map(f => 
            f.id === fileId ? { ...f, status: nextStatus } : f
          ));
          
          setTimeout(() => {
            setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
          }, result.processing ? 5000 : 2000);
          
          fetchUploadedFiles(selectedBotId); // Refresh the files list for current bot
          return true;
        } else {
          const message = await getUploadErrorMessage(response);
          console.error('Upload failed:', message);
          
          setUploadingFiles(prev => prev.map(f => 
            f.id === fileId ? { ...f, status: 'error', error: message } : f
          ));
          
          setTimeout(() => {
            setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
          }, 3000);
          
          alert(`Upload failed: ${message}`);
          return false;
        }
      } catch (error) {
        console.error('Upload error:', error);
        const message = error?.message?.includes('JSON')
          ? 'Upload failed: server returned an error page (often HTTP 413 — file too large for nginx). Ask admin to set client_max_body_size to at least 100m.'
          : (error?.message || 'Network error');
        
        setUploadingFiles(prev => prev.map(f => 
          f.id === fileId ? { ...f, status: 'error', error: message } : f
        ));
        
        setTimeout(() => {
          setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
        }, 3000);
        
        alert(`Upload failed: ${message}`);
        return false;
      }
    }, [userInfo.username, fetchUploadedFiles]);

    const downloadFile = async (fileId, fileName) => {
      try {
        const response = await authenticatedFetch(`/api/uploads/files/${fileId}/download`);
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          alert('Download failed');
        }
      } catch (error) {
        console.error('Download error:', error);
        alert('Download failed');
      }
    };

    const viewFile = async (file) => {
      setViewingFile(file);
      setViewFileLoading(true);
      setViewFileError("");
      setViewFileContent("");
      try {
        const response = await authenticatedFetch(`/api/uploads/files/${file.id}?preview=1`);
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message || "Failed to load file content");
        }
        const text = await response.text();
        setViewFileContent(text || "(No preview content)");
      } catch (error) {
        console.error("View file error:", error);
        setViewFileError(error.message || "Failed to load preview");
      } finally {
        setViewFileLoading(false);
      }
    };

    const deleteFileById = async (fileId) => {
      const response = await authenticatedFetch(`/api/uploads/files/${fileId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        let message = 'Delete failed';
        try {
          const err = await response.json();
          message = err.message || err.error || message;
        } catch (_) {
          /* ignore */
        }
        throw new Error(message);
      }
      return true;
    };

    const deleteFile = async (fileId) => {
      if (!window.confirm('Are you sure you want to delete this file?')) {
        return;
      }
      try {
        await deleteFileById(fileId);
        setSelectedFileIds((prev) => {
          const next = new Set(prev);
          next.delete(fileId);
          return next;
        });
        await fetchUploadedFiles(selectedBotId);
      } catch (error) {
        console.error('Delete error:', error);
        alert(error.message || 'Delete failed');
      }
    };

    const toggleFileSelection = (fileId, e) => {
      if (e) e.stopPropagation();
      setSelectedFileIds((prev) => {
        const next = new Set(prev);
        if (next.has(fileId)) {
          next.delete(fileId);
        } else {
          next.add(fileId);
        }
        return next;
      });
    };

    const toggleSelectAllFiles = () => {
      if (uploadedFiles.length === 0) return;
      const allOnPage = uploadedFiles.map((f) => f.id);
      const allSelected = allOnPage.every((id) => selectedFileIds.has(id));
      if (allSelected) {
        setSelectedFileIds((prev) => {
          const next = new Set(prev);
          allOnPage.forEach((id) => next.delete(id));
          return next;
        });
      } else {
        setSelectedFileIds((prev) => {
          const next = new Set(prev);
          allOnPage.forEach((id) => next.add(id));
          return next;
        });
      }
    };

    const handleDeleteSelectedFiles = async () => {
      if (selectedFileIds.size === 0) return;
      if (
        !window.confirm(
          `Delete ${selectedFileIds.size} selected file(s)? This removes them from the knowledge base.`
        )
      ) {
        return;
      }
      const ids = Array.from(selectedFileIds);
      let failed = 0;
      for (const fileId of ids) {
        try {
          await deleteFileById(fileId);
        } catch (error) {
          console.error('Delete error:', error);
          failed += 1;
        }
      }
      setSelectedFileIds(new Set());
      await fetchUploadedFiles(selectedBotId);
      if (failed > 0) {
        alert(`${ids.length - failed} file(s) deleted. ${failed} failed.`);
      } else {
        alert(`${ids.length} file(s) deleted successfully.`);
      }
    };

    // Drag and drop functionality
    useEffect(() => {
      if (activeTab !== 'uploads') return;

      const uploadArea = document.getElementById('uploadArea');
      const fileInput = document.getElementById('fileInput');

      if (!uploadArea || !fileInput) return;

      // Click to upload handler
      const handleClick = () => {
        fileInput.click();
      };

      // File input change handler
      const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (!selectedBotId) {
          alert('Please select a Bot ID before uploading files.');
          e.target.value = ''; // Reset input
          return;
        }
        files.forEach(file => uploadFile(file, selectedBotId));
        e.target.value = ''; // Reset input to allow same file upload
      };

      // Drag and drop handlers
      const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        uploadArea.classList.add('drag-over');
      };

      const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        uploadArea.classList.remove('drag-over');
      };

      const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        uploadArea.classList.remove('drag-over');

        const files = Array.from(e.dataTransfer.files);
        if (!selectedBotId) {
          alert('Please select a Bot ID before uploading files.');
          return;
        }
        files.forEach(file => uploadFile(file, selectedBotId));
      };

      // Add event listeners
      uploadArea.addEventListener('click', handleClick);
      fileInput.addEventListener('change', handleFileChange);
      uploadArea.addEventListener('dragover', handleDragOver);
      uploadArea.addEventListener('dragleave', handleDragLeave);
      uploadArea.addEventListener('drop', handleDrop);

      // Load files on component mount (start from page 1)
      fetchUploadedFiles(selectedBotId, 1, uploadPageSize);

      // Cleanup function
      return () => {
        uploadArea.removeEventListener('click', handleClick);
        fileInput.removeEventListener('change', handleFileChange);
        uploadArea.removeEventListener('dragover', handleDragOver);
        uploadArea.removeEventListener('dragleave', handleDragLeave);
        uploadArea.removeEventListener('drop', handleDrop);
      };
    }, [activeTab, uploadFile, fetchUploadedFiles, selectedBotId, kbSourceType]);

    // Load conversations when switching to conversations tab or bot_id changes
    useEffect(() => {
      if (activeTab === 'conversations') {
        fetchConversations();
      }
    }, [activeTab, fetchConversations, selectedBotId]);

    // Load predefined questions when switching to predefined tab or bot_id changes
    useEffect(() => {
      if (activeTab === 'predefined') {
        fetchPredefinedQuestions();
      }
    }, [activeTab, fetchPredefinedQuestions, selectedBotId]);

    // Load bot settings when switching to settings tab (only when authenticated to avoid 401)
    useEffect(() => {
      if (isAuthenticated && activeTab === 'settings') {
        fetchBotSettings(selectedBotId || null);
      }
    }, [isAuthenticated, activeTab, selectedBotId, fetchBotSettings]);

    // Load dashboard stats when on dashboard tab (debounce to avoid duplicate fetches)
    useEffect(() => {
      if (activeTab !== 'dashboard') return;
      if (dashboardStatsTimeoutRef.current) clearTimeout(dashboardStatsTimeoutRef.current);
      dashboardStatsTimeoutRef.current = setTimeout(() => {
        fetchDashboardStats();
        dashboardStatsTimeoutRef.current = null;
      }, 300);
      return () => {
        if (dashboardStatsTimeoutRef.current) clearTimeout(dashboardStatsTimeoutRef.current);
      };
    }, [activeTab, fetchDashboardStats, selectedBotId]);

    // Fetch bots function - defined early so it can be used in useEffect
    const fetchBots = useCallback(async () => {
      setIsLoadingBots(true);
      try {
        const response = await authenticatedFetch(`/api/bots`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.bots) {
            setBots(result.bots); // Update BotContext
            // Auto-select first bot if none selected
            if (!selectedBotId && result.bots.length > 0) {
              setSelectedBotId(result.bots[0].bot_id);
            }
          }
        } else {
          console.error('Failed to fetch bots');
        }
      } catch (error) {
        console.error('Error fetching bots:', error);
      } finally {
        setIsLoadingBots(false);
      }
    }, [selectedBotId, setBots, setSelectedBotId, setIsLoadingBots]);

    // Fetch bots on component mount
    useEffect(() => {
      fetchBots();
    }, [fetchBots]);

    // Load redirect questions for redirect tab and popup keyword validation in settings
    useEffect(() => {
      if (activeTab === 'redirect' || activeTab === 'settings') {
        fetchRedirectQuestions();
      }
    }, [activeTab, fetchRedirectQuestions, selectedBotId]);

    // Load escalated tickets when switching to escalated tab or bot_id changes
    useEffect(() => {
      if (activeTab === 'escalated') {
        fetchEscalatedTickets();
      }
    }, [activeTab, fetchEscalatedTickets, selectedBotId]);

    // Set admin context to disable session timeout for admin panel
    useEffect(() => {
      setAdminContext(true);

      // Cleanup: reset admin context when component unmounts
      return () => {
        setAdminContext(false);
      };
    }, [setAdminContext]);

    const renderDashboard = () => {
      const totalConversations = Number(dashboardStats.total_conversations || 0);
      const activeConversations = Number(dashboardStats.active_conversations || 0);
      const pendingConversations = Number(dashboardStats.pending_conversations || 0);
      const totalMessages = Number(dashboardStats.total_messages || 0);
      const totalFiles = Number(dashboardStats.total_files || 0);
      const activeAdmins = Number(dashboardStats.active_admins || 0);
      const activeQuestions = Number(dashboardStats.active_questions || 0);
      const resolvedRate = Math.max(0, Math.min(100, Number(dashboardStats.resolved_percentage || 0)));
      const avgMessagesPerConversation = Number(dashboardStats.avg_messages_per_conversation || 0);

      const resolvedConversations = Math.max(0, totalConversations - activeConversations - pendingConversations);
      const absolutePeak = Math.max(totalConversations, totalMessages, totalFiles, activeAdmins, activeQuestions, 1);
      const absoluteBars = [
        { key: "conversations", label: "Conversations", value: totalConversations, color: "#3b82f6" },
        { key: "messages", label: "Messages", value: totalMessages, color: "#7c3aed" },
        { key: "files", label: "Knowledge Files", value: totalFiles, color: "#06b6d4" },
        { key: "admins", label: "Active Admins", value: activeAdmins, color: "#16a34a" },
        { key: "questions", label: "Predefined Questions", value: activeQuestions, color: "#eab308" },
      ];

      const conversionBars = [
        { key: "total", label: "Total", value: totalConversations, color: "#60a5fa" },
        { key: "active", label: "Active", value: activeConversations, color: "#22c55e" },
        { key: "pending", label: "Pending", value: pendingConversations, color: "#f59e0b" },
        { key: "resolved", label: "Resolved", value: resolvedConversations, color: "#3b82f6" },
      ];

      const efficiencyBars = [
        {
          key: "resolved_rate",
          label: "Resolved Rate",
          percent: Math.max(0, Math.min(100, resolvedRate)),
        },
        {
          key: "recent_activity",
          label: "Recent Activity (7d / total)",
          percent: totalConversations > 0
            ? Math.max(0, Math.min(100, (Number(dashboardStats.recent_conversations || 0) / totalConversations) * 100))
            : 0,
        },
        {
          key: "message_density",
          label: "Message Density",
          percent: Math.max(0, Math.min(100, avgMessagesPerConversation * 10)),
        },
      ];

      const statusDonut = {
        active: totalConversations > 0 ? Math.round((activeConversations / totalConversations) * 100) : 0,
        pending: totalConversations > 0 ? Math.round((pendingConversations / totalConversations) * 100) : 0,
      };
      statusDonut.resolved = Math.max(0, 100 - statusDonut.active - statusDonut.pending);

      const resourceTotal = Math.max(1, totalFiles + activeAdmins + activeQuestions);
      const resourceDonut = {
        files: Math.round((totalFiles / resourceTotal) * 100),
        admins: Math.round((activeAdmins / resourceTotal) * 100),
        questions: Math.round((activeQuestions / resourceTotal) * 100),
      };

      return (
      <div className="dashboard-content">
        <div className="dashboard-header">
          <h2>Dashboard Overview</h2>
          <div className="dashboard-controls">
            {/* Bot ID Selector */}
            <div className="dashboard-control-group">
              <label className="dashboard-control-label">Select Bot ID:</label>
              <select
                className="dashboard-bot-select"
                value={selectedBotId || ''}
                onChange={(e) => setSelectedBotId(e.target.value)}
                disabled={isLoadingBots || bots.length === 0}
              >
                {isLoadingBots ? (
                  <option>Loading bots...</option>
                ) : bots.length === 0 ? (
                  <option>No bots available</option>
                ) : (
                  <>
                    <option value="">-- Select Bot --</option>
                    {bots.map((bot) => (
                      <option key={bot.bot_id} value={bot.bot_id}>
                        {bot.bot_id} {bot.bot_name ? `(${bot.bot_name})` : ''}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
            <button
              className="refresh-stats-btn"
              onClick={fetchDashboardStats}
              disabled={isLoadingStats || !selectedBotId}
              title={!selectedBotId ? "Please select a bot first" : "Refresh dashboard statistics"}
            >
              {isLoadingStats ? 'Loading...' : 'Refresh'}
            </button>
            <div className="dashboard-view-toggle" role="group" aria-label="Dashboard view mode">
              <button
                type="button"
                className={`dashboard-view-btn ${dashboardViewMode === "kpi" ? "active" : ""}`}
                onClick={() => setDashboardViewMode("kpi")}
              >
                KPI
              </button>
              <button
                type="button"
                className={`dashboard-view-btn ${dashboardViewMode === "charts" ? "active" : ""}`}
                onClick={() => setDashboardViewMode("charts")}
              >
                Charts
              </button>
            </div>
          </div>
          {!selectedBotId && (
            <div style={{
              marginTop: '12px', 
              padding: '12px', 
              backgroundColor: '#050505',
              border: '1px solid #2a2a2a',
              borderRadius: '6px',
              color: '#ffffff',
              fontSize: '14px'
            }}>
              Please select a Bot ID to view dashboard statistics and manage data.
            </div>
          )}
        </div>

        {dashboardViewMode === "kpi" ? (
        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-emoji" aria-hidden="true">💬</span>
            <div className="stat-info">
              <h3>Total Conversations</h3>
              <p className="stat-number">
                {isLoadingStats ? '...' : dashboardStats.total_conversations.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-emoji" aria-hidden="true">🟢</span>
            <div className="stat-info">
              <h3>Active conversations</h3>
              <p className="stat-number">
                {isLoadingStats ? '...' : dashboardStats.active_conversations.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-emoji" aria-hidden="true">🟠</span>
            <div className="stat-info">
              <h3>Pending conversations</h3>
              <p className="stat-number">
                {isLoadingStats ? '...' : dashboardStats.pending_conversations.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-emoji" aria-hidden="true">✅</span>
            <div className="stat-info">
              <h3>Resolved Rate</h3>
              <p className="stat-number">
                {isLoadingStats ? '...' : `${dashboardStats.resolved_percentage}%`}
              </p>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-emoji" aria-hidden="true">📨</span>
            <div className="stat-info">
              <h3>Total Messages</h3>
              <p className="stat-number">
                {isLoadingStats ? '...' : dashboardStats.total_messages.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-emoji" aria-hidden="true">📈</span>
            <div className="stat-info">
              <h3>Avg Conversations per week</h3>
              <p className="stat-number">
                {isLoadingStats ? '...' : dashboardStats.avg_messages_per_conversation}
              </p>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-emoji" aria-hidden="true">📁</span>
            <div className="stat-info">
              <h3>Files uploaded</h3>
              <p className="stat-number">
                {isLoadingStats ? '...' : dashboardStats.total_files.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-emoji" aria-hidden="true">👤</span>
            <div className="stat-info">
              <h3>Active Admins</h3>
              <p className="stat-number">
                {isLoadingStats ? '...' : dashboardStats.active_admins.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-emoji" aria-hidden="true">❔</span>
            <div className="stat-info">
              <h3>Pre-defined questions</h3>
              <p className="stat-number">
                {isLoadingStats ? '...' : dashboardStats.active_questions.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        ) : (
        <div className="dashboard-charts-grid">
          <div className="chart-card chart-card-wide">
            <div className="chart-card-header">
              <h3>Conversation Status Distribution</h3>
              <span>{totalConversations.toLocaleString()} total</span>
            </div>
            <div className="donut-layout">
              <div
                className="donut-chart donut-status"
                style={{
                  background: `conic-gradient(#22c55e 0 ${statusDonut.active}%, #f59e0b ${statusDonut.active}% ${statusDonut.active + statusDonut.pending}%, #3b82f6 ${statusDonut.active + statusDonut.pending}% 100%)`,
                }}
              >
                <div className="donut-inner">{`${Math.max(0, Math.min(100, resolvedRate))}%`}</div>
              </div>
              <div className="donut-legend">
                <div><span className="legend-dot active"></span>Active: {statusDonut.active}%</div>
                <div><span className="legend-dot pending"></span>Pending: {statusDonut.pending}%</div>
                <div><span className="legend-dot resolved"></span>Resolved: {statusDonut.resolved}%</div>
              </div>
            </div>
          </div>
          <div className="chart-card chart-card-wide">
            <div className="chart-card-header">
              <h3>Knowledge Resource Mix</h3>
              <span>{resourceTotal.toLocaleString()} total units</span>
            </div>
            <div className="donut-layout">
              <div
                className="donut-chart donut-resource"
                style={{
                  background: `conic-gradient(#a855f7 0 ${resourceDonut.files}%, #06b6d4 ${resourceDonut.files}% ${resourceDonut.files + resourceDonut.admins}%, #eab308 ${resourceDonut.files + resourceDonut.admins}% 100%)`,
                }}
              >
                <div className="donut-inner">Mix</div>
              </div>
              <div className="donut-legend">
                <div><span className="legend-dot files"></span>Files: {resourceDonut.files}%</div>
                <div><span className="legend-dot admins"></span>Admins: {resourceDonut.admins}%</div>
                <div><span className="legend-dot questions"></span>Questions: {resourceDonut.questions}%</div>
              </div>
            </div>
          </div>
          <div className="chart-card chart-card-wide">
            <div className="chart-card-header">
              <h3>Absolute Volume Comparison</h3>
              <span>Current totals</span>
            </div>
            <div className="stack-bars">
              {absoluteBars.map((row) => (
                <div key={row.key} className="stack-row">
                  <div className="stack-row-header">
                    <span>{row.label}</span>
                    <span>{isLoadingStats ? "..." : row.value.toLocaleString()}</span>
                  </div>
                  <div className="chart-track">
                    <div
                      className="chart-fill"
                      style={{
                        width: `${isLoadingStats ? 0 : Math.round((row.value / absolutePeak) * 100)}%`,
                        background: row.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="chart-card chart-card-wide">
            <div className="chart-card-header">
              <h3>Conversation Flow</h3>
              <span>From total to current state</span>
            </div>
            <div className="stack-bars">
              {conversionBars.map((row) => (
                <div key={row.key} className="stack-row">
                  <div className="stack-row-header">
                    <span>{row.label}</span>
                    <span>{isLoadingStats ? "..." : row.value.toLocaleString()}</span>
                  </div>
                  <div className="chart-track">
                    <div
                      className="chart-fill"
                      style={{
                        width: `${isLoadingStats || totalConversations === 0 ? 0 : Math.round((row.value / totalConversations) * 100)}%`,
                        background: row.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="chart-card chart-card-wide">
            <div className="chart-card-header">
              <h3>Operational Efficiency</h3>
              <span>Performance indicators</span>
            </div>
            <div className="stack-bars">
              {efficiencyBars.map((row) => (
                <div key={row.key} className="stack-row">
                  <div className="stack-row-header">
                    <span>{row.label}</span>
                    <span>{isLoadingStats ? "..." : `${Math.round(row.percent)}%`}</span>
                  </div>
                  <div className="chart-track">
                    <div
                      className="chart-fill"
                      style={{ width: `${isLoadingStats ? 0 : Math.round(row.percent)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        )}

        <div className="dashboard-footer">
          <div className="footer-stats">
            <span className="footer-stat">
              <strong>Recent Activity:</strong> {isLoadingStats ? '...' : dashboardStats.recent_conversations} conversations in last 7 days
            </span>
            <span className="footer-stat">
              <strong>File Storage:</strong> {isLoadingStats ? '...' : formatFileSize(dashboardStats.total_file_size)}
            </span>
          </div>
        </div>
      </div>
    );
    };

    const exportConversations = async (format = 'csv', conversationId = null, selectedIds = []) => {
      try {
        const token = getAuthToken();
        if (!token) {
          alert('Your session has expired. Please log in again.');
          return;
        }
        const params = new URLSearchParams({ format });
        if (!conversationId && conversationFilters.status !== 'all') {
          params.set('status', conversationFilters.status);
        }
        if (selectedBotId && !conversationId) {
          params.set('bot_id', selectedBotId);
        }
        if (selectedIds && selectedIds.length > 0) {
          params.set('ids', selectedIds.join(','));
        }
        // Fallback when reverse proxies strip Authorization (Bearer still sent via authenticatedFetch)
        params.set('access_token', token);
        const path = conversationId
          ? `/api/conversations/${encodeURIComponent(conversationId)}/export?${params}`
          : `/api/conversations/export?${params}`;
        const response = await authenticatedFetch(path, { method: 'GET' });
        if (!response.ok) {
          let errorMessage = 'Export failed';
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
          } catch (_) {
            /* ignore */
          }
          throw new Error(errorMessage);
        }
        const blob = await response.blob();
        const urlObject = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlObject;
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `conversations_export_${new Date().toISOString().slice(0, 10)}.${format}`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(urlObject);
        document.body.removeChild(a);
      } catch (error) {
        console.error('Export error:', error);
        alert(`Failed to export: ${error.message}`);
      }
    };

    const [selectedConversationIds, setSelectedConversationIds] = useState(new Set());

    const toggleConversationSelection = (conversationId, e) => {
      e.stopPropagation();
      setSelectedConversationIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(conversationId)) {
          newSet.delete(conversationId);
        } else {
          newSet.add(conversationId);
        }
        return newSet;
      });
    };

    const toggleSelectAll = (filteredConversations) => {
      if (selectedConversationIds.size === filteredConversations.length && filteredConversations.length > 0) {
        setSelectedConversationIds(new Set());
      } else {
        setSelectedConversationIds(new Set(filteredConversations.map(c => c.id)));
      }
    };

    const handleDeleteConversation = async (conversationId, e) => {
      if (e) e.stopPropagation();
      if (!window.confirm("Are you sure you want to delete this conversation? This action cannot be undone.")) {
        return;
      }

      try {
        const response = await authenticatedFetch(`/api/conversations/${conversationId}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          setConversations(prev => prev.filter(c => c.id !== conversationId));
          if (selectedConversation && selectedConversation.id === conversationId) {
            setSelectedConversation(null);
          }
          setSelectedConversationIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(conversationId);
            return newSet;
          });
          alert("Conversation deleted successfully.");
        } else {
          const error = await response.json();
          alert(`Failed to delete conversation: ${error.message}`);
        }
      } catch (error) {
        console.error("Error deleting conversation:", error);
        alert("Failed to delete conversation due to network error.");
      }
    };

    const handleDeleteSelectedConversations = async () => {
      if (selectedConversationIds.size === 0) return;

      if (!window.confirm(`Are you sure you want to delete ${selectedConversationIds.size} selected conversation(s)? This action cannot be undone.`)) {
        return;
      }

      try {
        const response = await authenticatedFetch(`/api/conversations/bulk`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ conversation_ids: Array.from(selectedConversationIds) })
        });

        if (response.ok) {
          setConversations(prev => prev.filter(c => !selectedConversationIds.has(c.id)));
          if (selectedConversation && selectedConversationIds.has(selectedConversation.id)) {
            setSelectedConversation(null);
          }
          setSelectedConversationIds(new Set());
          alert("Selected conversations deleted successfully.");
        } else {
          const error = await response.json();
          alert(`Failed to delete conversations: ${error.message}`);
        }
      } catch (error) {
        console.error("Error deleting conversations:", error);
        alert("Failed to delete conversations due to network error.");
      }
    };

    const renderConversations = () => {
      const filteredConversations = getFilteredConversations();
      const statusBuckets = conversations.reduce((acc, conversation) => {
        const status = (conversation.status || 'unknown').toLowerCase();
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      const statusSummary = [
        { key: 'active', label: 'Active', count: statusBuckets['active'] || 0 },
        { key: 'pending', label: 'Pending', count: statusBuckets['pending'] || 0 },
        { key: 'in-progress', label: 'In Progress', count: statusBuckets['in-progress'] || 0 },
        { key: 'resolved', label: 'Resolved', count: statusBuckets['resolved'] || 0 },
        { key: 'closed', label: 'Closed', count: statusBuckets['closed'] || 0 }
      ];

      const hasConversations = conversations.length > 0;
      const hasFiltered = filteredConversations.length > 0;

      return (
        <div className="ticketing-console">
          <div className="ticketing-header">
            <div className="ticketing-header-text">
              <h2>Support Tickets</h2>
              <p>Track, triage, and resolve customer conversations with a ticketing workflow.</p>
            </div>
            <div className="ticketing-actions">
              {selectedConversationIds.size > 0 && (
                <>
                  <button
                    className="ticketing-action-btn delete-all-btn"
                    onClick={handleDeleteSelectedConversations}
                    title="Delete selected conversations"
                    style={{ backgroundColor: '#fee2e2', color: '#dc2626', borderColor: '#fca5a5' }}
                  >
                    Delete ({selectedConversationIds.size})
                  </button>
                  <button
                    className="ticketing-action-btn"
                    onClick={() => setSelectedConversationIds(new Set())}
                    title="Clear selection"
                    style={{ backgroundColor: '#f1f5f9', color: '#475569', borderColor: '#cbd5e1' }}
                  >
                    Deselect ({selectedConversationIds.size})
                  </button>
                </>
              )}
              <button
                className="ticketing-action-btn"
                onClick={() => toggleSelectAll(filteredConversations)}
                title={selectedConversationIds.size === filteredConversations.length && filteredConversations.length > 0 ? "Deselect All" : "Select All"}
              >
                {selectedConversationIds.size === filteredConversations.length && filteredConversations.length > 0 ? "Unselect All" : "Select All"}
              </button>
              <button
                className="ticketing-action-btn"
                onClick={() => exportConversations('csv', null, Array.from(selectedConversationIds))}
                title="Export all or selected conversations as CSV"
              >
                Export CSV
              </button>
              <button
                className="ticketing-action-btn"
                onClick={() => exportConversations('json', null, Array.from(selectedConversationIds))}
                title="Export all or selected conversations as JSON"
              >
                Export JSON
              </button>
              <button
                className="ticketing-refresh-btn"
                onClick={() => {
                  // Refresh logic: Clear filters, clear selection, and fetch fresh data
                  setConversationFilters({
                    status: 'all',
                    name: '',
                    requestId: '',
                    dateFrom: '',
                    dateTo: '',
                    dateRange: ''
                  });
                  setSelectedConversationIds(new Set());
                  fetchConversations();
                }}
                title="Refresh conversations and clear filters"
              >
                Refresh
              </button>
            </div>
          </div>

          <div className="ticketing-metrics">
            {statusSummary.map((metric) => (
              <div key={metric.key} className={`ticketing-metric ${getStatusVariant(metric.key)}`}>
                <span className="metric-count">{metric.count}</span>
                <span className="metric-label">{metric.label}</span>
              </div>
            ))}
            <div className="ticketing-metric metric-total">
              <span className="metric-count">{conversations.length}</span>
              <span className="metric-label">Total Tickets</span>
            </div>
          </div>

          <div className="ticketing-body">
            <aside className="ticketing-sidebar">
              <div className="ticketing-filters">
                <div className="filter-title">Filters</div>
                <div className="filter-group">
                  <label htmlFor="status-filter">Status</label>
                  <select
                    id="status-filter"
                    value={conversationFilters.status || ''}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All tickets</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="in-progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="name-filter">Requester</label>
                  <input
                    id="name-filter"
                    type="text"
                    value={conversationFilters.name || ''}
                    onChange={(e) => handleFilterChange('name', e.target.value)}
                    placeholder="Search by name"
                    className="filter-input"
                  />
                </div>

                <div className="filter-group">
                  <label htmlFor="request-id-filter">Ticket ID</label>
                  <input
                    id="request-id-filter"
                    type="text"
                    value={conversationFilters.requestId || ''}
                    onChange={(e) => handleFilterChange('requestId', e.target.value)}
                    placeholder="Search ID"
                    className="filter-input"
                  />
                </div>

                <div className="filter-group">
                  <label htmlFor="date-range-filter">Created</label>
                  <select
                    id="date-range-filter"
                    onChange={(e) => setDateRange(e.target.value)}
                    className="filter-select"
                    value={conversationFilters.dateRange}
                  >
                    <option value="">Quick select</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="last7days">Last 7 days</option>
                    <option value="last30days">Last 30 days</option>
                    <option value="thisMonth">This month</option>
                    <option value="custom">Custom range</option>
                  </select>
                </div>

                <div className="filter-footer">
                  <button className="clear-filters-btn" onClick={clearFilters}>
                    Clear filters
                  </button>
                  <span className="filter-results">
                    {hasFiltered ? `${filteredConversations.length} of ${conversations.length}` : `${conversations.length}`} tickets
                  </span>
                </div>
              </div>

              {showCustomRangeModal && (
                <div className="custom-range-modal-overlay">
                  <div className="custom-range-modal">
                    <div className="modal-header">
                      <h3>Select Custom Date Range</h3>
                      <button className="modal-close-btn" onClick={handleCustomRangeCancel}>
                        Remove
                      </button>
                    </div>

                    <div className="modal-content">
                      <div className="modal-form-group">
                        <label htmlFor="custom-from-date">From Date:</label>
                        <input
                          id="custom-from-date"
                          type="date"
                          value={customRangeDates.from || ''}
                          onChange={(e) => setCustomRangeDates(prev => ({ ...prev, from: e.target.value }))}
                          className="modal-date-input"
                        />
                      </div>

                      <div className="modal-form-group">
                        <label htmlFor="custom-to-date">To Date:</label>
                        <input
                          id="custom-to-date"
                          type="date"
                          value={customRangeDates.to || ''}
                          onChange={(e) => setCustomRangeDates(prev => ({ ...prev, to: e.target.value }))}
                          className="modal-date-input"
                        />
                      </div>
                    </div>

                    <div className="modal-actions">
                      <button className="modal-cancel-btn" onClick={handleCustomRangeCancel}>
                        Cancel
                      </button>
                      <button
                        className="modal-submit-btn"
                        onClick={handleCustomRangeSubmit}
                        disabled={!customRangeDates.from || !customRangeDates.to}
                      >
                        Apply Range
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </aside>

            <section className="ticketing-main">
              <div className="ticketing-main-columns">
                <div className="ticket-list">
                  {!hasConversations ? (
                    <div className="ticket-empty-state">
                      <h3>No tickets yet</h3>
                      <p>Start a conversation from the chatbot to populate your queue.</p>
                    </div>
                  ) : !hasFiltered ? (
                    <div className="ticket-empty-state">
                      <h3>No matches</h3>
                      <p>Adjust your filters to find the ticket you are looking for.</p>
                    </div>
                  ) : (
                    filteredConversations.map((conversation) => {
                      const priority = getPriorityLevel(conversation);
                      const isActive = selectedConversation && selectedConversation.id === conversation.id;
                      const isSelected = selectedConversationIds.has(conversation.id);
                      return (
                        <div
                          key={conversation.id}
                          className={`ticket-card ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''}`}
                          onClick={() => fetchConversationDetails(conversation.id)}
                        >
                          <div className="ticket-card-header" style={{ display: 'flex', alignItems: 'center' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => toggleConversationSelection(conversation.id, e)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ marginRight: '10px', height: '16px', width: '16px', cursor: 'pointer' }}
                            />
                            <span className={`ticket-status ${getStatusVariant(conversation.status)}`}>
                              {conversation.status ? conversation.status.replace('-', ' ') : 'Unknown'}
                            </span>
                            <span className="ticket-id">#{conversation.request_id || conversation.id.slice(-8)}</span>
                            <button
                              className="delete-ticket-btn"
                              onClick={(e) => handleDeleteConversation(conversation.id, e)}
                              title="Delete this conversation"
                              style={{
                                marginLeft: 'auto',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '16px',
                                opacity: 0.6
                              }}
                            >
                              Delete
                            </button>
                          </div>
                          <div className="ticket-card-title">{conversation.userName || 'Unknown requester'}</div>
                          <div className="ticket-card-preview">
                            {conversation.lastMessage.length > 120
                              ? `${conversation.lastMessage.substring(0, 120)}...`
                              : conversation.lastMessage || 'No messages yet.'}
                          </div>
                          <div className="ticket-card-meta">
                            <span className={`ticket-priority priority-${priority}`}>
                              {priority.charAt(0).toUpperCase() + priority.slice(1)} priority
                            </span>
                            <span className="ticket-messages">{conversation.messageCount || 0} msgs</span>
                            <span className="ticket-updated">
                              {formatRelativeTime(conversation.lastMessageTime || conversation.startTime)}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="ticket-detail">
                  {selectedConversation ? (
                    <div className="ticket-detail-wrapper">
                      <div className="ticket-detail-header">
                        <div className="ticket-detail-heading">
                          <span className="ticket-detail-id">Ticket #{selectedConversation.request_id || selectedConversation.id.slice(-8)}</span>
                          <h3>{selectedConversation.userName || 'Unknown requester'}</h3>
                          <div className="ticket-detail-meta">
                            <label>Status</label>
                            <select
                              className={`ticket-status-select status-select ${(selectedConversation.status || 'active').toLowerCase().replace(/\s+/g, '-')}`}
                              value={selectedConversation.status || ''}
                              onChange={(e) => updateConversationStatus(selectedConversation.id, e.target.value)}
                            >
                              <option value="active">Active</option>
                              <option value="pending">Pending</option>
                              <option value="in-progress">In Progress</option>
                              <option value="resolved">Resolved</option>
                              <option value="closed">Closed</option>
                            </select>
                            <span className={`ticket-priority-chip priority-${getPriorityLevel(selectedConversation)}`}>
                              {getPriorityLevel(selectedConversation).toUpperCase()} PRIORITY
                            </span>
                            <span className="ticket-message-count">
                              {selectedConversation.messageCount || selectedConversation.messages.length} messages
                            </span>
                          </div>
                        </div>
                        <div className="ticket-detail-actions">
                          <button
                            className="ticketing-action-btn"
                            onClick={() => exportConversations('csv', selectedConversation.id)}
                            title="Export this ticket as CSV"
                          >
                            CSV
                          </button>
                          <button
                            className="ticketing-action-btn"
                            onClick={() => exportConversations('json', selectedConversation.id)}
                            title="Export this ticket as JSON"
                          >
                            JSON
                          </button>
                        </div>
                      </div>

                      <div className="ticket-summary">
                        <div className="summary-item">
                          <span className="summary-label">Created</span>
                          <span className="summary-value">
                            {formatDate(selectedConversation.startTime || selectedConversation.created_at)}
                          </span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Last activity</span>
                          <span className="summary-value">
                            {formatDate(selectedConversation.lastMessageTime || selectedConversation.updated_at)}
                          </span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Requester</span>
                          <span className="summary-value">{selectedConversation.userName || 'Unknown'}</span>
                        </div>
                      </div>

                      <div className="ticket-activity">
                        <h4>Conversation timeline</h4>
                        <div className="activity-feed">
                          {(selectedConversation.messages || []).map((message, index) => (
                            <div key={index} className={`activity-event ${message.type}`}>
                              <div className="event-header">
                                <span className="event-author">
                                  {message.type === 'user'
                                    ? `User ${selectedConversation.userName || 'User'}`
                                    : 'Bot'}
                                </span>
                                <span className="event-time">{formatDate(message.timestamp)}</span>
                              </div>
                              <div className="event-body">{message.text}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="ticket-empty-detail">
                      <h3>Select a ticket to view details</h3>
                      <p>Choose a ticket from the left panel to review the conversation timeline.</p>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      );
    };

    const renderPredefinedQuestions = () => (
      <div className="predefined-questions-content">
        <div className="predefined-header">
          <h2>Pre-defined Questions</h2>
          <button
            className="add-question-btn"
            onClick={() => setShowQuestionForm(true)}
          >
            Add New Question
          </button>
        </div>

        {showQuestionForm && (
          <QuestionForm
            question={editingQuestion}
            onSubmit={editingQuestion ? handleEditQuestion : createPredefinedQuestion}
            onCancel={() => {
              setShowQuestionForm(false);
              setEditingQuestion(null);
            }}
          />
        )}

        <div className="questions-list">
          {predefinedQuestions.length === 0 ? (
            <div className="no-questions">
              <p>No predefined questions yet. Create one to start collecting user information!</p>
            </div>
          ) : (
            predefinedQuestions.map((question) => (
              <div key={question.id} id="question-card" className="question-card">
                <div className="question-header">
                  <span className={`question-type ${question.questionType}`}>
                    {question.questionType.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className={`question-status ${question.isActive ? 'active' : 'inactive'}`}>
                    {question.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="question-content">
                  <h4>{question.questionText}</h4>
                  {question.options && question.options.length > 0 && (
                    <div className="question-options">
                      <strong>Options:</strong>
                      <ul>
                        {question.options.map((option, index) => (
                          <li key={index}>{option}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="question-meta">
                    <span className="question-order">Order: {question.orderIndex}</span>
                  </div>
                </div>
                <div className="question-actions">
                  <button
                    className="edit-btn"
                    onClick={() => {
                      setEditingQuestion(question);
                      setShowQuestionForm(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => deletePredefinedQuestion(question.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );

    // Repetitive Question Form Component
    const RepetitiveQuestionForm = ({ question, onSubmit, onCancel }) => {
      const [formData, setFormData] = useState({
        question: question?.question || '',
        order_index: question?.order_index || 0,
        is_active: question?.is_active !== undefined ? question.is_active : true
      });

      const handleInputChange = (field, value) => {
        setFormData(prev => ({
          ...prev,
          [field]: value
        }));
      };

      const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.question.trim()) {
          alert('Question is required!');
          return;
        }

        onSubmit(formData);
      };

      return (
        <div className="question-form-overlay">
          <div className="question-form" style={{ maxWidth: '800px' }}>
            <h3>{question ? 'Edit Suggested Question' : 'Add New Suggested Question'}</h3>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Question *</label>
                <textarea
                  value={formData.question || ''}
                  onChange={(e) => handleInputChange('question', e.target.value)}
                  placeholder="Enter the suggested question..."
                  required
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Order Index</label>
                <input
                  type="number"
                  value={formData.order_index || 0}
                  onChange={(e) => handleInputChange('order_index', parseInt(e.target.value) || 0)}
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  />
                  Active (will show in chat suggestions)
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-btn">
                  {question ? 'Update' : 'Create'} Question
                </button>
                <button type="button" onClick={onCancel} className="cancel-btn">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      );
    };

    const renderRepetitiveQuestions = () => (
      <div className="predefined-questions-content">
        <div className="predefined-header">
          <h2>Suggested Questions</h2>
          <p style={{ fontSize: '14px', color: '#64748b', marginTop: '8px' }}>
            These questions will auto-populate as suggestions when users open the chat input
          </p>
          <button
            className="add-question-btn"
            onClick={() => setShowRepetitiveForm(true)}
          >
            Add New Suggested Question
          </button>
        </div>

        {showRepetitiveForm && (
          <RepetitiveQuestionForm
            question={editingRepetitiveQuestion}
            onSubmit={editingRepetitiveQuestion ? handleEditRepetitiveQuestion : createRepetitiveQuestion}
            onCancel={() => {
              setShowRepetitiveForm(false);
              setEditingRepetitiveQuestion(null);
            }}
          />
        )}

        <div className="questions-list">
          {repetitiveQuestions.length === 0 ? (
            <div className="no-questions">
              <p>No suggested questions yet. Create one to help users with common questions!</p>
            </div>
          ) : (
            repetitiveQuestions.map((question) => (
              <div key={question.id} className="question-card">
                <div className="question-header">
                  <span className={`question-status ${question.is_active ? 'active' : 'inactive'}`}>
                    {question.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className="question-order">Order: {question.order_index}</span>
                </div>
                <div className="question-content">
                  <div>
                    <h4 style={{ marginTop: '4px', color: '#1e293b' }}>{question.question}</h4>
                  </div>
                </div>
                <div className="question-actions">
                  <button
                    className="edit-btn"
                    onClick={() => {
                      setEditingRepetitiveQuestion(question);
                      setShowRepetitiveForm(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => deleteRepetitiveQuestion(question.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );

    const renderSettings = () => (
      <div className="settings-content">
        <div className="settings-header">
          <h2>ChatBot Configuration</h2>
          <p>Customize your chatbot's appearance and behavior</p>
          <div className="bot-selector" style={{ marginTop: '15px' }}>
            <label htmlFor="bot-select" style={{ marginRight: '10px', fontWeight: 'bold' }}>
              Select Bot:
            </label>
            <select
              id="bot-select"
              value={selectedBotId || ''}
              onChange={(e) => {
                setSelectedBotId(e.target.value || null);
                fetchBotSettings(e.target.value || null);
              }}
              style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', minWidth: '200px' }}
              disabled={bots.length === 0}
            >
              <option value="">All Bots (Default)</option>
              {bots.length === 0 ? (
                <option value="" disabled>Loading bots...</option>
              ) : (
                bots.map((bot) => (
                  <option key={bot.bot_id} value={bot.bot_id}>
                    {bot.bot_name} {bot.is_enabled ? '' : '(Disabled)'}
                  </option>
                ))
              )}
            </select>
            {selectedBotId && (
              <button
                className="btn-small btn-secondary"
                onClick={() => {
                  setSelectedBotId(null);
                  fetchBotSettings(null);
                }}
                style={{ marginLeft: '10px' }}
              >
                Clear Selection
              </button>
            )}
            {bots.length === 0 && (
              <span style={{ marginLeft: '10px', color: '#6b7280', fontSize: '14px' }}>
                (Loading bots...)
              </span>
            )}
          </div>
        </div>

        <div className="settings-form">
          {/* Basic Settings Section */}
          <div className="settings-section">
            <h3 className="section-title">Basic Settings</h3>

            <div className="setting-group">
              <label htmlFor="bot-name">Company Name / Bot Display Name *</label>
              <input
                id="bot-name"
                type="text"
                value={(botSettings && botSettings.bot_name) ?? ''}
                onChange={(e) => handleSettingsChange('bot_name', e.target.value)}
                placeholder="e.g., TechSupport Bot, Customer Care Assistant"
                required
              />
              <small className="setting-help">This name will appear as the chatbot title in the chat interface</small>
            </div>

            <div className="setting-group">
              <label htmlFor="welcome-message">Welcome Message</label>
              <textarea
                id="welcome-message"
                value={(botSettings && botSettings.welcome_message) ?? ''}
                onChange={(e) => handleSettingsChange('welcome_message', e.target.value)}
                placeholder="Enter the message users will see when they start a chat..."
                rows="4"
              />
              <small className="setting-help">This message will be shown when users first open the chat</small>
            </div>

            <div className="setting-group">
              <label htmlFor="chat-icon-enabled">Chat Icon</label>
              <div className="toggle-row">
                <label className="switch">
                  <input
                    id="chat-icon-enabled"
                    type="checkbox"
                    checked={botSettings?.chat_icon_enabled !== false}
                    onChange={(e) => handleSettingsChange('chat_icon_enabled', e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
                <span className="toggle-label">
                  {botSettings?.chat_icon_enabled !== false ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <small className="setting-help">
                Show an icon in the chat header and next to bot messages. Turn off for text-only branding.
              </small>
            </div>

            {botSettings?.chat_icon_enabled !== false && (
              <div className="setting-group">
                <label htmlFor="chat-icon">Icon character or image URL</label>
                <input
                  id="chat-icon"
                  type="text"
                  maxLength={512}
                  value={(botSettings && botSettings.chat_icon) ?? '💬'}
                  onChange={(e) => handleSettingsChange('chat_icon', e.target.value)}
                />
                <small className="setting-help">
                  Emoji (e.g. 💬), a single letter, or an image URL for the header and bot avatars.
                </small>
              </div>
            )}
          </div>

          {/* Behavior Settings Section */}
          <div className="settings-section">
            <h3 className="section-title">Behavior Settings</h3>

            <div className="setting-group">
              <label htmlFor="response-delay">Response Delay</label>
              <select
                id="response-delay"
                value={botSettings.response_delay_seconds ?? 1.0}
                onChange={(e) => handleSettingsChange('response_delay_seconds', parseFloat(e.target.value))}
              >
                <option value={0.5}>0.5 seconds - Very Fast</option>
                <option value={1.0}>1.0 second - Normal</option>
                <option value={1.5}>1.5 seconds - Thoughtful</option>
                <option value={2.0}>2.0 seconds - Deliberate</option>
              </select>
              <small className="setting-help">How long to wait before showing bot responses</small>
            </div>

            <div className="setting-group">
              <label htmlFor="survey-enabled">Enable Survey</label>
              <div className="toggle-row">
                <label className="switch">
                  <input
                    id="survey-enabled"
                    type="checkbox"
                    checked={!!botSettings.survey_enabled}
                    onChange={(e) => handleSettingsChange('survey_enabled', e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
                <span className="toggle-label">{botSettings.survey_enabled ? 'Enabled' : 'Disabled'}</span>
              </div>
              <small className="setting-help">Turn off to skip the survey and allow users to chat immediately</small>
            </div>

            <div className="setting-group">
              <label htmlFor="font-family">Font Family</label>
              <select
                id="font-family"
                value={botSettings.theme_font_family || 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'}
                onChange={(e) => handleSettingsChange('theme_font_family', e.target.value)}
              >
                <option value="system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, Noto Sans, Apple Color Emoji, Segoe UI Emoji">System UI</option>
                <option value="Segoe UI, Tahoma, Geneva, Verdana, sans-serif">Segoe UI (Windows)</option>
                <option value="Roboto, Helvetica, Arial, sans-serif">Roboto (Android)</option>
                <option value="Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif">Inter</option>
                <option value="Open Sans, Helvetica, Arial, sans-serif">Open Sans</option>
                <option value="Poppins, Helvetica, Arial, sans-serif">Poppins</option>
                <option value="Nunito, Helvetica, Arial, sans-serif">Nunito</option>
                <option value="IBM Plex Sans, Helvetica, Arial, sans-serif">IBM Plex Sans</option>
                <option value="Georgia, Times, Times New Roman, serif">Georgia (Serif)</option>
                <option value="'Courier New', Courier, monospace">Courier New (Monospace)</option>
              </select>
              <small className="setting-help">Choose a font stack; users see the closest available font on their device.</small>
            </div>
          </div>

          {/* Theme Customization Section */}
          <div className="settings-section">
            <h3 className="section-title">Theme Customization</h3>
            <ThemeCustomizer
              settings={botSettings}
              onSettingsChange={handleSettingsChange}
            />
          </div>

          {/* Popup Configuration Section */}
          <div className="settings-section">
            <h3 className="section-title">Popup Configuration</h3>
            <p className="section-description">
              Show a promotional message or link when the user asks a question that matches your trigger keywords (same keyword matching as Redirect Questions).
            </p>

            <div className="setting-group">
              <label htmlFor="popup-enabled">Enable Popup</label>
              <div className="toggle-row">
                <label className="switch">
                  <input
                    id="popup-enabled"
                    type="checkbox"
                    checked={!!botSettings.popup_enabled}
                    onChange={(e) => handleSettingsChange('popup_enabled', e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
                <span className="toggle-label">{botSettings.popup_enabled ? 'Enabled' : 'Disabled'}</span>
              </div>
            </div>

            {botSettings.popup_enabled && (
              <>
                <div className="setting-group">
                  <label htmlFor="popup-type">Popup Type</label>
                  <select
                    id="popup-type"
                    value={botSettings.popup_type || 'message'}
                    onChange={(e) => handleSettingsChange('popup_type', e.target.value)}
                  >
                    <option value="message">Message</option>
                    <option value="link">Link Button</option>
                  </select>
                </div>

                <div className="setting-group">
                  <label htmlFor="popup-message">Popup Message</label>
                  <textarea
                    id="popup-message"
                    rows="3"
                    value={botSettings.popup_message || ''}
                    onChange={(e) => handleSettingsChange('popup_message', e.target.value)}
                    placeholder="Enter the popup message to show in chat"
                  />
                </div>

                {botSettings.popup_type === 'link' && (
                  <>
                    <div className="setting-group">
                      <label htmlFor="popup-link-text">Link Text</label>
                      <input
                        id="popup-link-text"
                        type="text"
                        value={botSettings.popup_link_text || ''}
                        onChange={(e) => handleSettingsChange('popup_link_text', e.target.value)}
                        placeholder="e.g., View Details"
                      />
                    </div>
                    <div className="setting-group">
                      <label htmlFor="popup-link-url">Link URL</label>
                      <input
                        id="popup-link-url"
                        type="url"
                        value={botSettings.popup_link_url || ''}
                        onChange={(e) => handleSettingsChange('popup_link_url', e.target.value)}
                        placeholder="https://example.com/promo"
                      />
                    </div>
                  </>
                )}

                <div className="setting-group">
                  <label htmlFor="popup-trigger-words">Trigger Keywords (comma separated)</label>
                  <input
                    id="popup-trigger-words"
                    type="text"
                    value={botSettings.popup_trigger_words || ''}
                    onChange={(e) => handleSettingsChange('popup_trigger_words', e.target.value)}
                    placeholder="pricing, demo, quote, lims"
                  />
                  <small className="setting-help">
                    Popup appears when the user message contains any of these keywords (case-insensitive).
                    Keywords must not overlap with Redirect Questions for this bot.
                  </small>
                  {(() => {
                    const overlaps = findKeywordOverlaps(
                      botSettings.popup_trigger_words,
                      collectRedirectKeywords(redirectQuestions)
                    );
                    if (!overlaps.length) return null;
                    return (
                      <small className="setting-help" style={{ color: '#f87171', display: 'block', marginTop: 6 }}>
                        Conflict: {overlaps.map((k) => `"${k}"`).join(', ')} already used in Redirect Questions.
                      </small>
                    );
                  })()}
                </div>

                <div className="setting-group">
                  <label htmlFor="popup-dismissible">Dismissible</label>
                  <div className="toggle-row">
                    <label className="switch">
                      <input
                        id="popup-dismissible"
                        type="checkbox"
                        checked={!!botSettings.popup_dismissible}
                        onChange={(e) => handleSettingsChange('popup_dismissible', e.target.checked)}
                      />
                      <span className="slider round"></span>
                    </label>
                    <span className="toggle-label">{botSettings.popup_dismissible ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Display Settings Section */}
          <div className="settings-section">
            <h3 className="section-title">Display Settings</h3>

            <div className="setting-group">
              <label htmlFor="window-size">Window Size</label>
              <select
                id="window-size"
                value={botSettings.window_size || 'medium'}
                onChange={(e) => handleSettingsChange('window_size', e.target.value)}
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="fullscreen">Full-screen</option>
              </select>
              <small className="setting-help">Controls the chat widget size on the page</small>
            </div>

            <div className="setting-group">
              <label htmlFor="sound-enabled">Sound Notifications</label>
              <div className="toggle-row">
                <label className="switch">
                  <input
                    id="sound-enabled"
                    type="checkbox"
                    checked={!!botSettings.sound_enabled}
                    onChange={(e) => handleSettingsChange('sound_enabled', e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
                <span className="toggle-label">{botSettings.sound_enabled ? 'On' : 'Off'}</span>
              </div>
              <small className="setting-help">Play a short sound for each new message</small>
            </div>
          </div>



          <div className="settings-actions">
            <button
              className={`save-button ${isUpdatingSettings ? 'loading' : ''}`}
              onClick={handleSaveSettings}
              disabled={isUpdatingSettings || !botSettings.bot_name.trim()}
            >
              {isUpdatingSettings ? 'Saving...' : 'Save Settings'}
            </button>

            {botSettings.bot_name && (
              <>
                {selectedBotId && (
                  <div className="embed-code-section" style={{ marginTop: '20px', padding: '15px', background: '#050505', borderRadius: '8px', border: '1px solid #1f1f1f' }}>
                    <h4 style={{ marginTop: 0 }}>Integration Code</h4>
                    <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                      Use this code to embed the chatbot on your website. Make sure to select a bot above before copying.
                    </p>
                    <p style={{ fontSize: '13px', color: '#d1d5db', marginBottom: '10px', fontWeight: 500 }}>
                      Add this script to your <strong>shared layout or footer</strong> (the template included on every page) so the chatbot appears on <strong>all pages</strong>. If you only add it to the home page, the chatbot will show only there.
                    </p>
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '10px' }}>
                      The widget auto-detects fixed/sticky site navigation bars and keeps the chat header below them. If needed, add <code style={{ color: '#e5e7eb' }}>data-host-inset-top="72"</code> as a minimum offset (pixels) for unusual layouts.
                    </p>
                    <div style={{ background: '#000000', padding: '12px', borderRadius: '4px', border: '1px solid #2a2a2a', marginBottom: '10px', color: '#ffffff' }}>
                      <code style={{ fontSize: '13px', wordBreak: 'break-all' }}>
                        {`<script src="${(WIDGET_BASE_URL || (typeof window !== 'undefined' && window.location.origin) || '')}/chatbot/widget.js" data-bot-id="${selectedBotId}"></script>`}
                      </code>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <button
                        onClick={() => {
                          const base = WIDGET_BASE_URL || (typeof window !== 'undefined' && window.location.origin) || '';
                          const code = `<script src="${base}/chatbot/widget.js" data-bot-id="${selectedBotId}"></script>`;
                          navigator.clipboard.writeText(code);
                          alert('Embed code copied to clipboard!');
                        }}
                        style={{
                          padding: '8px 16px',
                          background: '#2563eb',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        Copy Code
                      </button>
                      <a
                        href={`${window.location.pathname.startsWith('/chatbot') ? '/chatbot' : ''}/?bot_id=${encodeURIComponent(selectedBotId)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '8px 16px',
                          background: '#10b981',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '4px',
                          fontSize: '14px',
                          display: 'inline-block'
                        }}
                        title="Opens the same widget as end-users: floating owl bubble and close button"
                      >
                        Test Widget
                      </a>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );

    const renderUploads = () => {
      const totalFiles = uploadTotal;
      const totalSize = uploadedFiles.reduce((acc, file) => acc + file.fileSize, 0);
      const recentFiles = uploadedFiles.slice(0, 3); // Show most recent 3 files in summary
      const totalPages = Math.max(1, Math.ceil((uploadTotal || 0) / uploadPageSize));

      return (
        <>
        <div className="uploads-content">
          {/* Header Section */}
          <div className="uploads-header">
            <div className="header-main">
              <h2>File Management</h2>
              <p className="header-subtitle">Upload and manage documents for your knowledge base</p>
            </div>
            <div className="file-stats">
              <div className="stat-item">
                <span className="stat-number">{totalFiles}</span>
                <span className="stat-label">Files</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{formatFileSize(totalSize)}</span>
                <span className="stat-label">Total Size</span>
              </div>
            </div>
          </div>

          {/* Upload Area - Enhanced (only for File Upload mode) */}
          {kbSourceType === "file_upload" && (
          <div className="upload-section">
            <div className="upload-card">
              <div className="upload-header">
                <h3>Upload New Files</h3>
                <p>Add documents to expand your knowledge base</p>
                <div style={{ marginTop: '15px', marginBottom: '10px' }}>
                  <label htmlFor="upload-bot-select" style={{ marginRight: '10px', fontWeight: 'bold', fontSize: '14px' }}>
                    Select Bot ID (Required): 
                  </label>
                  <select
                    id="upload-bot-select"
                    value={selectedBotId || ''}
                    onChange={(e) => setSelectedBotId(e.target.value || null)}
                    style={{ 
                      padding: '8px 12px', 
                      borderRadius: '4px', 
                      border: '1px solid #ddd', 
                      minWidth: '200px',
                      fontSize: '14px'
                    }}
                    required
                  >
                    <option value="">-- Select Bot ID --</option>
                    {bots.length === 0 ? (
                      <option value="" disabled>Loading bots...</option>
                    ) : (
                      bots.map((bot) => (
                        <option key={bot.bot_id} value={bot.bot_id}>
                          {bot.bot_name} ({bot.bot_id}) {bot.is_enabled ? '' : '(Disabled)'}
                        </option>
                      ))
                    )}
                  </select>
                  {!selectedBotId && (
                    <span style={{ marginLeft: '10px', color: '#ef4444', fontSize: '12px' }}>
                      Please select a bot ID before uploading files
                    </span>
                  )}
                </div>
              </div>
              <div className="upload-area" id="uploadArea">
                <div className="upload-content">
                  <div className="upload-icon" aria-hidden="true"></div>
                  <div className="upload-text">
                    <h4>Drop files here</h4>
                    <p>or click to browse your computer</p>
                    <p className="upload-hint">PDF, Word, Excel, CSV, JSON, or text — up to {MAX_UPLOAD_MB} MB per file</p>
                  </div>
                </div>
                <input
                  type="file"
                  id="fileInput"
                  multiple
                  accept=".doc,.docx,.pdf,.json,.txt,.xls,.xlsx,.csv"
                  style={{ display: 'none' }}
                />
              </div>
              {/* Upload Progress Section */}
              {uploadingFiles.length > 0 && (
                <div className="upload-progress-section">
                  <h4 className="upload-progress-title">Uploading Files</h4>
                  <div className="upload-progress-list">
                    {uploadingFiles.map((file) => (
                      <div key={file.id} className={`upload-progress-item ${file.status || 'uploading'}`}>
                        <div className="upload-progress-info">
                          <div className="upload-progress-icon">
                            {(file.status === 'uploading' || file.status === 'processing') && (
                              <div className="upload-spinner">
                                <div className="spinner-circle"></div>
                              </div>
                            )}
                            {file.status === 'success' && <span className="success-icon">✓</span>}
                            {file.status === 'error' && <span className="error-icon">✗</span>}
                          </div>
                          <div className="upload-progress-details">
                            <div className="upload-file-name">{file.name}</div>
                            <div className="upload-file-size">
                              {formatFileSize(file.size)}
                              {file.status === 'uploading' && ' — Uploading…'}
                              {file.status === 'processing' && ' — Indexing knowledge base…'}
                            </div>
                            {file.error && (
                              <div className="upload-error-message">{file.error}</div>
                            )}
                          </div>
                        </div>
                        <div className="upload-progress-bar-container">
                          <div 
                            className={`upload-progress-bar ${file.status}`}
                            style={{ 
                              width: file.status === 'uploading' ? '100%' : 
                                    file.status === 'success' ? '100%' : '100%'
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          )}
              <div
                className="kb-external-imports"
                style={{
                  marginTop: 18,
                  padding: 16,
                  border: "1px solid #2a2a2a",
                  borderRadius: 12,
                  background: "#050505",
                  color: "#ffffff",
                }}
              >
                <h3 style={{ margin: "0 0 12px 0", color: "#ffffff" }}>External Knowledge Sources</h3>
                <div className="kb-source-tabs">
                  {[
                    { id: "file_upload", label: "File Upload" },
                    { id: "database", label: "Database Import" },
                    { id: "web_crawl", label: "Web Crawl" },
                    { id: "object_storage", label: "Object Storage" },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={`kb-source-tab ${kbSourceType === opt.id ? "active" : ""}`}
                      onClick={() => setKbSourceType(opt.id)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
                  {/* DB Import */}
                  {kbSourceType === "database" && (
                  <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 14 }}>
                    <h4 style={{ marginTop: 0, marginBottom: 10 }}>Database Import</h4>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <label style={{ fontSize: 12, color: "#64748b" }}>DB Type</label>
                      <select
                        value={kbDbForm.db_type}
                        onChange={(e) => setKbDbForm((p) => ({ ...p, db_type: e.target.value }))}
                        style={{ padding: 8, borderRadius: 8, border: "1px solid #e5e7eb" }}
                      >
                        <option value="postgres">PostgreSQL</option>
                        <option value="mysql">MySQL</option>
                        <option value="mssql">MSSQL</option>
                        <option value="sqlite">SQLite</option>
                      </select>

                      <label style={{ fontSize: 12, color: "#64748b" }}>Host</label>
                      <input
                        value={kbDbForm.host}
                        onChange={(e) => setKbDbForm((p) => ({ ...p, host: e.target.value }))}
                        placeholder="db host"
                        style={{ padding: 8, borderRadius: 8, border: "1px solid #e5e7eb" }}
                      />

                      <label style={{ fontSize: 12, color: "#64748b" }}>Port</label>
                      <input
                        value={kbDbForm.port ?? ""}
                        onChange={(e) => setKbDbForm((p) => ({ ...p, port: e.target.value }))}
                        placeholder="5432"
                        type="text"
                        style={{ padding: 8, borderRadius: 8, border: "1px solid #e5e7eb" }}
                      />

                      <label style={{ fontSize: 12, color: "#64748b" }}>Database</label>
                      <input
                        value={kbDbForm.database}
                        onChange={(e) => setKbDbForm((p) => ({ ...p, database: e.target.value }))}
                        placeholder="dbname"
                        style={{ padding: 8, borderRadius: 8, border: "1px solid #e5e7eb" }}
                      />

                      <label style={{ fontSize: 12, color: "#64748b" }}>Username</label>
                      <input
                        value={kbDbForm.username}
                        onChange={(e) => setKbDbForm((p) => ({ ...p, username: e.target.value }))}
                        placeholder="user"
                        style={{ padding: 8, borderRadius: 8, border: "1px solid #e5e7eb" }}
                      />

                      <label style={{ fontSize: 12, color: "#64748b" }}>Password</label>
                      <input
                        value={kbDbForm.password}
                        onChange={(e) => setKbDbForm((p) => ({ ...p, password: e.target.value }))}
                        placeholder="••••••••"
                        type="password"
                        style={{ padding: 8, borderRadius: 8, border: "1px solid #e5e7eb" }}
                      />
                    </div>

                    <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                      <button
                        className="action-btn-text"
                        disabled={!selectedBotId || kbDbLoading}
                        onClick={async () => {
                          if (!selectedBotId) {
                            alert("Please select a Bot ID first.");
                            return;
                          }
                          const dbType = (kbDbForm.db_type || "").toLowerCase();
                          const needsNetworkFields = dbType !== "sqlite";
                          if (!kbDbForm.database || !String(kbDbForm.database).trim()) {
                            alert("Please enter the database name/path.");
                            return;
                          }
                          if (needsNetworkFields) {
                            if (!kbDbForm.host || !String(kbDbForm.host).trim()) {
                              alert("Please enter DB host.");
                              return;
                            }
                            if (!kbDbForm.username || !String(kbDbForm.username).trim()) {
                              alert("Please enter DB username.");
                              return;
                            }
                            if (!kbDbForm.password || !String(kbDbForm.password).trim()) {
                              alert("Please enter DB password.");
                              return;
                            }
                          }
                          setKbDbLoading(true);
                          setKbDbError("");
                          try {
                            const resp = await authenticatedFetch("/api/knowledge/db/connections", {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({
                                bot_id: selectedBotId,
                                name: kbDbForm.name,
                                db_type: kbDbForm.db_type,
                                credentials: {
                                  host: kbDbForm.host,
                                  port: kbDbForm.port ? parseInt(kbDbForm.port, 10) : null,
                                  database: kbDbForm.database,
                                  username: kbDbForm.username,
                                  password: kbDbForm.password,
                                },
                                options: { schema: kbDbForm.schema || null },
                              }),
                            });
                            if (!resp.ok) {
                              const err = await resp.json().catch(() => ({}));
                              const msg = err.message || "Failed to connect DB.";
                              setKbDbError(msg);
                              alert(msg);
                              return;
                            }
                            const res = await resp.json();
                            setKbDbConnectionId(res.connectionId || null);
                            setKbDbTables([]);
                          } catch (e) {
                            console.error(e);
                            const msg = "DB connection failed. Check server logs.";
                            setKbDbError(msg);
                            alert(msg);
                          } finally {
                            setKbDbLoading(false);
                          }
                        }}
                        style={{ minWidth: 180 }}
                      >
                        {kbDbLoading ? "Connecting..." : "Connect & Test"}
                      </button>
                      {kbDbError && (
                        <div style={{ width: "100%", marginTop: 8, color: "#b91c1c", fontSize: 12 }}>
                          DB connect error: {kbDbError}
                        </div>
                      )}
                    </div>

                    {kbDbConnectionId && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                          <strong style={{ fontSize: 12, color: "#059669" }}>Connection ready</strong>
                          <span style={{ fontSize: 12, color: "#64748b" }}>Tables are fetched from your DB (server-side).</span>
                        </div>

                        <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                          <button
                            className="action-btn-text"
                            onClick={async () => {
                              try {
                                const resp = await authenticatedFetch(
                                  `/api/knowledge/db/connections/${kbDbConnectionId}/tables?page=${kbDbTablesPage}&pageSize=${kbDbTablesPageSize}`
                                );
                                if (!resp.ok) {
                                  alert("Failed to load tables.");
                                  return;
                                }
                                const res = await resp.json();
                                setKbDbTables(res.tables || []);
                                setKbDbTablesTotal(res.total || 0);
                                setKbDbSelectedTables([]);
                                setKbDbTableSearchQuery("");
                              } catch (e) {
                                console.error(e);
                                alert("Failed to load tables.");
                              }
                            }}
                          >
                            Load Tables
                          </button>

                          <div style={{ width: "100%", marginTop: 6 }}>
                            <label style={{ fontSize: 12, color: "#64748b" }}>Search Tables</label>
                            <input
                              value={kbDbTableSearchQuery}
                              onChange={(e) => setKbDbTableSearchQuery(e.target.value)}
                              placeholder="e.g., users, tickets"
                              style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e5e7eb", marginTop: 6 }}
                            />

                            <div
                              style={{
                                marginTop: 10,
                                border: "1px solid #e5e7eb",
                                borderRadius: 10,
                                padding: 10,
                                maxHeight: 180,
                                overflowY: "auto",
                              }}
                            >
                              {kbDbTables.length === 0 && (
                                <div style={{ fontSize: 12, color: "#64748b", padding: "8px 4px" }}>
                                  No tables loaded yet. Click "Load Tables".
                                </div>
                              )}
                              {(kbDbTableSearchQuery
                                ? kbDbTables.filter((t) => (t || "").toLowerCase().includes(kbDbTableSearchQuery.toLowerCase()))
                                : kbDbTables
                              ).map((t) => (
                                <label
                                  key={t}
                                  style={{
                                    display: "flex",
                                    gap: 10,
                                    alignItems: "center",
                                    padding: "6px 8px",
                                    borderRadius: 8,
                                    cursor: "pointer",
                                    background: kbDbSelectedTables.includes(t) ? "#ecfdf5" : "transparent",
                                    border: kbDbSelectedTables.includes(t) ? "1px solid #10b981" : "1px solid transparent",
                                    marginBottom: 6,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={kbDbSelectedTables.includes(t)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setKbDbSelectedTables((prev) => Array.from(new Set([...prev, t])));
                                      } else {
                                        setKbDbSelectedTables((prev) => prev.filter((x) => x !== t));
                                      }
                                    }}
                                  />
                                  <span style={{ fontSize: 13, color: "#111827" }}>{t}</span>
                                </label>
                              ))}

                              {kbDbTables.length > 0 &&
                                (kbDbTableSearchQuery
                                  ? kbDbTables.filter((t) => (t || "").toLowerCase().includes(kbDbTableSearchQuery.toLowerCase())).length === 0
                                  : false) && (
                                  <div style={{ fontSize: 12, color: "#64748b", padding: "8px 4px" }}>
                                    No matching tables on this page.
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>

                        {kbDbTablesTotal > 0 && (
                          <div style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                            <button
                              className="action-btn-text"
                              disabled={kbDbTablesPage <= 1}
                              onClick={() => setKbDbTablesPage((p) => Math.max(1, p - 1))}
                            >
                              Prev
                            </button>
                            <span style={{ fontSize: 12, color: "#64748b" }}>
                              Page {kbDbTablesPage} of {Math.max(1, Math.ceil(kbDbTablesTotal / kbDbTablesPageSize))}
                            </span>
                            <button
                              className="action-btn-text"
                              disabled={kbDbTablesPage >= Math.ceil(kbDbTablesTotal / kbDbTablesPageSize)}
                              onClick={() => setKbDbTablesPage((p) => p + 1)}
                            >
                              Next
                            </button>
                            <label style={{ fontSize: 12, color: "#64748b" }}>
                              Page size:
                              <select
                                value={kbDbTablesPageSize}
                                onChange={(e) => { setKbDbTablesPageSize(parseInt(e.target.value, 10)); setKbDbTablesPage(1); }}
                                style={{ marginLeft: 6, padding: 6, borderRadius: 8, border: "1px solid #e5e7eb" }}
                              >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                              </select>
                            </label>
                          </div>
                        )}

                        <div style={{ marginTop: 10 }}>
                          <label style={{ fontSize: 12, color: "#64748b" }}>Import Mode</label>
                          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 6 }}>
                            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
                              <input type="radio" checked={kbDbMode === "entire_table"} onChange={() => setKbDbMode("entire_table")} />
                              Entire table
                            </label>
                            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
                              <input type="radio" checked={kbDbMode === "choose_columns"} onChange={() => setKbDbMode("choose_columns")} />
                              Choose columns
                            </label>
                          </div>

                          {kbDbMode === "choose_columns" && (
                            <div style={{ marginTop: 8 }}>
                              <label style={{ fontSize: 12, color: "#64748b" }}>Columns (comma-separated)</label>
                              <input
                                value={kbDbColumnsText}
                                onChange={(e) => setKbDbColumnsText(e.target.value)}
                                placeholder="col1, col2, col3"
                                style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e5e7eb", marginTop: 6 }}
                              />
                            </div>
                          )}

                          <div style={{ marginTop: 8 }}>
                            <label style={{ fontSize: 12, color: "#64748b" }}>Max rows to import (safety cap)</label>
                            <input
                              type="number"
                              value={kbDbMaxRows ?? ""}
                              onChange={(e) => setKbDbMaxRows(parseInt(e.target.value || 0, 10))}
                              style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e5e7eb", marginTop: 6 }}
                            />
                          </div>

                          <button
                            className="action-btn-text"
                            style={{ marginTop: 10 }}
                            onClick={async () => {
                              if (!kbDbConnectionId || kbDbSelectedTables.length === 0) {
                                alert("Select at least one table first.");
                                return;
                              }
                              try {
                              const cols = kbDbMode === "choose_columns"
                                ? kbDbColumnsText.split(",").map(s => s.trim()).filter(Boolean)
                                : [];
                              let lastJobId = null;
                              for (const tableName of kbDbSelectedTables) {
                                const payload = {
                                  bot_id: selectedBotId,
                                  connection_id: kbDbConnectionId,
                                  table_name: tableName,
                                  mode: kbDbMode,
                                  columns: cols,
                                  max_rows: kbDbMaxRows,
                                };
                                const resp = await authenticatedFetch("/api/knowledge/db/import", {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify(payload),
                                });
                                if (!resp.ok) {
                                  const err = await resp.json().catch(() => ({}));
                                  alert(`Failed for table "${tableName}": ${err.message || "Failed to start import."}`);
                                  continue;
                                }
                                const res = await resp.json();
                                lastJobId = res.jobId || lastJobId;
                              }
                              setKbDbJobId(lastJobId);
                              setKbDbJobStatus(null);
                            } catch (e) {
                              console.error(e);
                              alert("Failed to start DB import.");
                            }
                          }}
                            disabled={kbDbSelectedTables.length === 0}
                          >
                            Start DB Import ({kbDbSelectedTables.length})
                          </button>
                        </div>

                        {kbDbJobId && (
                          <div style={{ marginTop: 10, fontSize: 13 }}>
                            <button
                              className="action-btn-text"
                              onClick={async () => {
                                try {
                                  const resp = await authenticatedFetch(`/api/knowledge/import/jobs/${kbDbJobId}`);
                                  if (!resp.ok) {
                                    alert("Failed to fetch job status.");
                                    return;
                                  }
                                  const res = await resp.json();
                                  setKbDbJobStatus(res.job || null);
                                } catch (e) {
                                  console.error(e);
                                  alert("Failed to fetch job status.");
                                }
                              }}
                            >
                              Refresh Job Status
                            </button>
                            <div style={{ marginTop: 8, color: "#64748b" }}>
                              Status: <strong>{kbDbJobStatus?.status || "queued"}</strong>
                              {kbDbJobStatus?.progress?.percent !== undefined && (
                                <> • {kbDbJobStatus.progress.percent}%</>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  )}

                  {/* Web Crawl */}
                  {kbSourceType === "web_crawl" && (
                  <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 14 }}>
                    <h4 style={{ marginTop: 0, marginBottom: 10 }}>Web Crawl</h4>
                    <label style={{ fontSize: 12, color: "#64748b" }}>Seed URL</label>
                    <input
                      value={kbCrawlForm.seed_url ?? ""}
                      onChange={(e) => setKbCrawlForm((p) => ({ ...p, seed_url: e.target.value }))}
                      placeholder="https://example.com"
                      style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e5e7eb", marginTop: 6 }}
                    />

                    <div style={{ display: "flex", gap: 12, marginTop: 10, flexWrap: "wrap" }}>
                      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
                        <input
                          type="checkbox"
                          checked={kbCrawlForm.respect_robots}
                          onChange={(e) => setKbCrawlForm((p) => ({ ...p, respect_robots: e.target.checked }))}
                        />
                        Respect robots.txt
                      </label>
                      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
                        <input
                          type="checkbox"
                          checked={kbCrawlForm.schedule_enabled}
                          onChange={(e) => setKbCrawlForm((p) => ({ ...p, schedule_enabled: e.target.checked }))}
                        />
                        Enable scheduled crawl
                      </label>
                    </div>

                    {kbCrawlForm.schedule_enabled && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                        <div>
                          <label style={{ fontSize: 12, color: "#64748b" }}>Repeat</label>
                          <select
                            value={kbCrawlForm.schedule_interval_unit || "daily"}
                            onChange={(e) => setKbCrawlForm((p) => ({ ...p, schedule_interval_unit: e.target.value }))}
                            style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e5e7eb", marginTop: 6 }}
                          >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: 12, color: "#64748b" }}>
                            Every {kbCrawlForm.schedule_interval_unit === "weekly" ? "N weeks" : "N days"}
                          </label>
                          <input
                            type="number"
                            min={1}
                            value={kbCrawlForm.schedule_interval_value ?? 1}
                            onChange={(e) => setKbCrawlForm((p) => ({ ...p, schedule_interval_value: parseInt(e.target.value || 1, 10) }))}
                            style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e5e7eb", marginTop: 6 }}
                          />
                        </div>
                        {kbCrawlForm.schedule_interval_unit === "weekly" && (
                          <div>
                            <label style={{ fontSize: 12, color: "#64748b" }}>Weekday</label>
                            <select
                              value={kbCrawlForm.schedule_weekday ?? 1}
                              onChange={(e) => setKbCrawlForm((p) => ({ ...p, schedule_weekday: parseInt(e.target.value, 10) }))}
                              style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e5e7eb", marginTop: 6 }}
                            >
                              <option value={0}>Monday</option>
                              <option value={1}>Tuesday</option>
                              <option value={2}>Wednesday</option>
                              <option value={3}>Thursday</option>
                              <option value={4}>Friday</option>
                              <option value={5}>Saturday</option>
                              <option value={6}>Sunday</option>
                            </select>
                          </div>
                        )}
                        <div>
                          <label style={{ fontSize: 12, color: "#64748b" }}>Time (UTC) — schedule only</label>
                          <input
                            type="time"
                            value={kbCrawlForm.schedule_time_utc || "00:00"}
                            onChange={(e) => setKbCrawlForm((p) => ({ ...p, schedule_time_utc: e.target.value }))}
                            style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e5e7eb", marginTop: 6 }}
                          />
                        </div>
                      </div>
                    )}

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                      <div>
                        <label style={{ fontSize: 12, color: "#64748b" }}>Max pages</label>
                        <input
                          type="number"
                          value={kbCrawlForm.max_pages ?? ""}
                          onChange={(e) => setKbCrawlForm((p) => ({ ...p, max_pages: parseInt(e.target.value || 0, 10) }))}
                          style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e5e7eb", marginTop: 6 }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "#64748b" }}>Max depth</label>
                        <input
                          type="number"
                          value={kbCrawlForm.max_depth ?? ""}
                          onChange={(e) => setKbCrawlForm((p) => ({ ...p, max_depth: parseInt(e.target.value || 0, 10) }))}
                          style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e5e7eb", marginTop: 6 }}
                        />
                      </div>
                    </div>

                    <button
                      className="action-btn-text action-btn-text--block"
                      style={{ marginTop: 12 }}
                      disabled={!selectedBotId || kbCrawlLoading}
                      onClick={async () => {
                        if (!selectedBotId) {
                          alert("Please select a Bot ID first.");
                          return;
                        }
                        if (!kbCrawlForm.seed_url) {
                          alert("Please provide a seed URL.");
                          return;
                        }
                        setKbCrawlLoading(true);
                        setKbCrawlStatusError("");
                        try {
                          const resp = await authenticatedFetch("/api/knowledge/webcrawl/config", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              bot_id: selectedBotId,
                              seed_url: kbCrawlForm.seed_url,
                              respect_robots: kbCrawlForm.respect_robots,
                              max_pages: kbCrawlForm.max_pages,
                              max_depth: kbCrawlForm.max_depth,
                              schedule_enabled: kbCrawlForm.schedule_enabled,
                              schedule_interval_unit: kbCrawlForm.schedule_interval_unit,
                              schedule_interval_value: kbCrawlForm.schedule_interval_value,
                              schedule_weekday: kbCrawlForm.schedule_weekday,
                              schedule_time_utc: kbCrawlForm.schedule_time_utc,
                            }),
                          });
                          if (!resp.ok) {
                            const err = await resp.json().catch(() => ({}));
                            alert(err.message || "Failed to create crawl config.");
                            return;
                          }
                          const res = await resp.json();
                          const configId = res.configId;
                          const runResp = await authenticatedFetch(`/api/knowledge/webcrawl/config/${configId}/run-now`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({}),
                          });
                          if (!runResp.ok) {
                            const err2 = await runResp.json().catch(() => ({}));
                            alert(err2.message || "Failed to run crawl.");
                            return;
                          }
                          const runRes = await runResp.json();
                          const newRunId = runRes.runId || null;
                          setKbCrawlRunId(newRunId);
                          setKbCrawlStatusError('');
                          setKbCrawlStatusNotice('Crawl started. Refreshing status…');

                          if (newRunId) {
                            try {
                              const run = await fetchKbCrawlRunStatus(newRunId);
                              if (run?.status === 'running') {
                                setKbCrawlStatusNotice('Crawl is running. Status updates automatically.');
                              } else if (run?.status === 'completed') {
                                await fetchUploadedFiles(selectedBotId);
                                setKbCrawlStatusNotice('Crawl completed. Files library updated.');
                              }
                            } catch (_) {
                              setKbCrawlStatusNotice('Crawl started. Click Refresh Crawl Status to check progress.');
                            }
                          }
                        } catch (e) {
                          console.error(e);
                          setKbCrawlStatusError("Failed to start/track crawl run.");
                          alert("Crawl failed. Check server logs.");
                        } finally {
                          setKbCrawlLoading(false);
                        }
                      }}
                    >
                      Start Crawl
                    </button>
                    <p style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
                      After the crawl completes, new sources should appear in Files Library and will be used for answers.
                    </p>
                    {(kbCrawlRunId || kbCrawlRunStatus) && (
                      <div style={{ marginTop: 10, border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#f8fafc" }}>
                        <div style={{ fontSize: 12, color: "#475569" }}><strong>Latest Crawl Run</strong></div>
                        <div style={{ fontSize: 12, color: "#334155", marginTop: 6 }}>Run ID: {kbCrawlRunStatus?.id || kbCrawlRunId}</div>
                        <div style={{ fontSize: 12, color: "#334155", marginTop: 4 }}>Seed URL: {kbCrawlRunStatus?.seedUrl || kbCrawlForm.seed_url}</div>
                        <div style={{ fontSize: 12, color: "#334155", marginTop: 4 }}>Status: {kbCrawlRunStatus?.status || "running"}</div>
                        <div style={{ fontSize: 12, color: "#334155", marginTop: 4 }}>
                          Pages crawled: {kbCrawlRunStatus?.pagesCrawled ?? "-"} | Pages changed: {kbCrawlRunStatus?.pagesChanged ?? "-"}
                        </div>
                        {kbCrawlRunStatus?.startedAt && (
                          <div style={{ fontSize: 12, color: "#334155", marginTop: 4 }}>
                            Started: {formatDate(kbCrawlRunStatus.startedAt)}
                            {kbCrawlRunStatus.finishedAt ? ` · Finished: ${formatDate(kbCrawlRunStatus.finishedAt)}` : ''}
                          </div>
                        )}
                        {kbCrawlRunStatus?.status === 'failed' && kbCrawlRunStatus?.errorMessage && (
                          <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 6 }}>Error: {kbCrawlRunStatus.errorMessage}</div>
                        )}
                        {kbCrawlStatusError && (
                          <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 6 }}>{kbCrawlStatusError}</div>
                        )}
                        {kbCrawlStatusNotice && !kbCrawlStatusError && (
                          <div style={{ fontSize: 12, color: "#166534", marginTop: 6 }}>{kbCrawlStatusNotice}</div>
                        )}
                        <button
                          type="button"
                          className="action-btn-text"
                          style={{ marginTop: 8 }}
                          disabled={kbCrawlRefreshing || !(kbCrawlRunStatus?.id || kbCrawlRunId)}
                          onClick={handleRefreshCrawlStatus}
                        >
                          {kbCrawlRefreshing ? 'Refreshing…' : 'Refresh Crawl Status'}
                        </button>
                      </div>
                    )}
                  </div>
                  )}

                  {/* Object Storage (MVP - selected keys) */}
                  {kbSourceType === "object_storage" && (
                  <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 14 }}>
                    <h4 style={{ marginTop: 0, marginBottom: 10 }}>Object Storage Import</h4>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <label style={{ fontSize: 12, color: "#64748b" }}>Provider</label>
                      <select
                        value={kbOsForm.provider}
                        onChange={(e) => setKbOsForm((p) => ({ ...p, provider: e.target.value }))}
                        style={{ padding: 8, borderRadius: 8, border: "1px solid #e5e7eb" }}
                      >
                        <option value="s3">S3</option>
                        <option value="gcs">GCS</option>
                        <option value="azure">Azure</option>
                      </select>

                      <label style={{ fontSize: 12, color: "#64748b" }}>Bucket</label>
                      <input
                        value={kbOsForm.bucket}
                        onChange={(e) => setKbOsForm((p) => ({ ...p, bucket: e.target.value }))}
                        placeholder="bucket"
                        style={{ padding: 8, borderRadius: 8, border: "1px solid #e5e7eb" }}
                      />

                      <label style={{ fontSize: 12, color: "#64748b" }}>Prefix (optional)</label>
                      <input
                        value={kbOsForm.prefix}
                        onChange={(e) => setKbOsForm((p) => ({ ...p, prefix: e.target.value }))}
                        placeholder="docs/"
                        style={{ padding: 8, borderRadius: 8, border: "1px solid #e5e7eb" }}
                      />
                    </div>
                        <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
                          Selected tables: {kbDbSelectedTables.length}
                        </div>

                    <div style={{ marginTop: 10 }}>
                      {kbOsForm.provider === "s3" && (
                        <>
                          <label style={{ fontSize: 12, color: "#64748b" }}>Access Key ID</label>
                          <input
                            value={kbOsForm.access_key_id}
                            onChange={(e) => setKbOsForm((p) => ({ ...p, access_key_id: e.target.value }))}
                            style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e5e7eb", marginTop: 6 }}
                          />
                          <label style={{ fontSize: 12, color: "#64748b", marginTop: 10, display: "block" }}>Secret Access Key</label>
                          <input
                            value={kbOsForm.secret_access_key}
                            onChange={(e) => setKbOsForm((p) => ({ ...p, secret_access_key: e.target.value }))}
                            type="password"
                            style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e5e7eb", marginTop: 6 }}
                          />
                          <label style={{ fontSize: 12, color: "#64748b", marginTop: 10, display: "block" }}>Region</label>
                          <input
                            value={kbOsForm.region}
                            onChange={(e) => setKbOsForm((p) => ({ ...p, region: e.target.value }))}
                            placeholder="us-east-1"
                            style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e5e7eb", marginTop: 6 }}
                          />
                        </>
                      )}
                    </div>

                    <button
                      className="action-btn-text"
                      style={{ marginTop: 12, width: "100%" }}
                      disabled={!selectedBotId}
                      onClick={async () => {
                        if (!selectedBotId) {
                          alert("Please select a Bot ID first.");
                          return;
                        }
                        try {
                          const resp = await authenticatedFetch("/api/knowledge/os/connections", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              bot_id: selectedBotId,
                              name: kbOsForm.name,
                              provider: kbOsForm.provider,
                              bucket: kbOsForm.bucket,
                              prefix: kbOsForm.prefix || null,
                              credentials:
                                kbOsForm.provider === "s3"
                                  ? {
                                      access_key_id: kbOsForm.access_key_id,
                                      secret_access_key: kbOsForm.secret_access_key,
                                      region: kbOsForm.region || null,
                                      session_token: kbOsForm.session_token || null,
                                    }
                                  : {},
                              options: {},
                            }),
                          });
                          if (!resp.ok) {
                            const err = await resp.json().catch(() => ({}));
                            alert(err.message || "Failed to connect object storage.");
                            return;
                          }
                          const res = await resp.json();
                          setKbOsConnectionId(res.connectionId || null);
                          alert(`Storage connected. Objects test count: ${res.testListedCount || 0}`);
                        } catch (e) {
                          console.error(e);
                          alert("Object storage connection failed.");
                        }
                      }}
                    >
                      Connect Storage
                    </button>

                    {kbOsConnectionId && (
                      <div style={{ marginTop: 12 }}>
                        <label style={{ fontSize: 12, color: "#64748b" }}>Object keys (comma-separated)</label>
                        <textarea
                          value={kbOsObjectKeysText}
                          onChange={(e) => setKbOsObjectKeysText(e.target.value)}
                          placeholder="docs/a.pdf, docs/b.txt"
                          style={{ width: "100%", height: 70, padding: 8, borderRadius: 8, border: "1px solid #e5e7eb", marginTop: 6 }}
                        />
                        <label style={{ fontSize: 12, color: "#64748b", display: "block", marginTop: 8 }}>Max objects</label>
                        <input
                          type="number"
                          value={kbOsMaxObjects}
                          onChange={(e) => setKbOsMaxObjects(parseInt(e.target.value || 0, 10))}
                          style={{ width: "100%", padding: 8, borderRadius: 8, border: "1px solid #e5e7eb", marginTop: 6 }}
                        />
                        <button
                          className="action-btn-text"
                          style={{ marginTop: 10, width: "100%" }}
                          onClick={async () => {
                            const keys = kbOsObjectKeysText
                              .split(/[,\n]/)
                              .map((s) => s.trim())
                              .filter(Boolean);
                            if (!keys.length) {
                              alert("Enter object keys first.");
                              return;
                            }
                            const payload = {
                              bot_id: selectedBotId,
                              connection_id: kbOsConnectionId,
                              object_keys: keys,
                              max_objects: kbOsMaxObjects,
                            };
                            const resp = await authenticatedFetch("/api/knowledge/os/import", {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify(payload),
                            });
                            if (!resp.ok) {
                              const err = await resp.json().catch(() => ({}));
                              alert(err.message || "Failed to start OS import.");
                              return;
                            }
                            const res = await resp.json();
                            setKbOsJobId(res.jobId || null);
                            alert(`OS import started. Job: ${res.jobId}`);
                          }}
                        >
                          Start OS Import
                        </button>
                      </div>
                    )}
                  </div>
                  )}
                  {/* File upload panel is shown above via the upload section when kbSourceType === "file_upload" */}
                </div>
              </div>

              <div className="supported-types">
                <div className="types-label">Supported file types:</div>
                <div className="file-types-grid">
                  <span className="file-type-tag">PDF</span>
                  <span className="file-type-tag">Word (.doc, .docx)</span>
                  <span className="file-type-tag">Excel (.xls, .xlsx)</span>
                  <span className="file-type-tag">CSV</span>
                  <span className="file-type-tag">TXT</span>
                  <span className="file-type-tag">JSON</span>
                </div>
                <p className="supported-types-note" style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', marginBottom: 0 }}>
                  The chatbot answers using content extracted from these files. Upload PDF, Word, Excel, CSV, JSON, or text to train the knowledge base.
                </p>
              </div>
            </div>

          {/* Files Management Section */}
          <div className="files-section">
            <div className="files-header">
              <div className="files-title-section">
                <h3>Files Library</h3>
                <p className="files-count">{totalFiles > 0 ? `${totalFiles} file${totalFiles !== 1 ? 's' : ''} uploaded` : 'No files yet'}</p>
              </div>
              {uploadedFiles.length > 0 && (
                <div className="files-toolbar">
                  {selectedFileIds.size > 0 && (
                    <>
                      <button
                        type="button"
                        className="action-btn-text files-toolbar-btn files-toolbar-btn-danger"
                        onClick={handleDeleteSelectedFiles}
                        title="Delete selected files"
                      >
                        Delete ({selectedFileIds.size})
                      </button>
                      <button
                        type="button"
                        className="action-btn-text files-toolbar-btn"
                        onClick={() => setSelectedFileIds(new Set())}
                        title="Clear selection"
                      >
                        Deselect ({selectedFileIds.size})
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className="action-btn-text files-toolbar-btn"
                    onClick={toggleSelectAllFiles}
                    title={
                      uploadedFiles.every((f) => selectedFileIds.has(f.id)) && uploadedFiles.length > 0
                        ? 'Deselect all on this page'
                        : 'Select all on this page'
                    }
                  >
                    {uploadedFiles.every((f) => selectedFileIds.has(f.id)) && uploadedFiles.length > 0
                      ? 'Unselect All'
                      : 'Select All'}
                  </button>
                </div>
              )}
              {totalFiles > 0 && (
                <div className="files-pagination" style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="action-btn-text"
                    onClick={() => {
                      const next = Math.max(1, uploadPage - 1);
                      setUploadPage(next);
                      fetchUploadedFiles(selectedBotId, next, uploadPageSize);
                    }}
                    disabled={uploadPage <= 1}
                  >
                    Prev
                  </button>
                  <span style={{ fontSize: 13, color: "#64748b" }}>
                    Page <strong>{uploadPage}</strong> of <strong>{totalPages}</strong>
                  </span>
                  <button
                    type="button"
                    className="action-btn-text"
                    onClick={() => {
                      const next = Math.min(totalPages, uploadPage + 1);
                      setUploadPage(next);
                      fetchUploadedFiles(selectedBotId, next, uploadPageSize);
                    }}
                    disabled={uploadPage >= totalPages}
                  >
                    Next
                  </button>
                  <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: "#64748b" }}>
                    Page size:
                    <select
                      value={uploadPageSize}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setUploadPageSize(v);
                        setUploadPage(1);
                        fetchUploadedFiles(selectedBotId, 1, v);
                      }}
                      style={{ padding: "6px 10px", borderRadius: 6 }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </label>
                </div>
              )}
              {recentFiles.length > 0 && (
                <div className="recent-uploads">
                  <span>Recent:</span>
                  <div className="recent-files">
                    {recentFiles.map((file, index) => (
                      <span key={file.id} className="recent-file-tag" title={file.originalName}>
                        {file.originalName.length > 15 ? `${file.originalName.substring(0, 15)}...` : file.originalName}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="files-table-container">
              {uploadedFiles.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">No Files</div>
                  <h4>No files uploaded yet</h4>
                  <p>Upload your first document to get started with the knowledge base</p>
                </div>
              ) : (
                <table className="files-table">
                  <thead>
                    <tr>
                      <th className="col-select" title="Select files">
                        <input
                          type="checkbox"
                          checked={
                            uploadedFiles.length > 0 &&
                            uploadedFiles.every((f) => selectedFileIds.has(f.id))
                          }
                          onChange={toggleSelectAllFiles}
                          aria-label="Select all files on this page"
                        />
                      </th>
                      <th className="col-name">File Name</th>
                      <th className="col-type">Type</th>
                      <th className="col-size">Size</th>
                      <th className="col-user">Uploaded By</th>
                      <th className="col-date">Upload Date</th>
                      <th className="col-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadedFiles.map((file) => (
                      <tr
                        key={file.id}
                        className={`file-row${selectedFileIds.has(file.id) ? ' selected' : ''}`}
                      >
                        <td className="col-select">
                          <input
                            type="checkbox"
                            checked={selectedFileIds.has(file.id)}
                            onChange={(e) => toggleFileSelection(file.id, e)}
                            aria-label={`Select ${file.originalName}`}
                          />
                        </td>
                        <td className="file-name">
                          <div className="file-info">
                            <span className="file-icon">
                              {file.fileType === 'pdf' ? 'PDF' :
                                file.fileType === 'doc' || file.fileType === 'docx' ? 'DOC' :
                                  file.fileType === 'xls' || file.fileType === 'xlsx' ? 'XLS' :
                                    file.fileType === 'txt' ? 'TXT' : 'FILE'}
                            </span>
                            <span className="file-name-text" title={file.originalName}>
                              {file.originalName}
                            </span>
                          </div>
                        </td>
                        <td className="file-type">
                          <span className="type-badge">{file.fileType.toUpperCase()}</span>
                        </td>
                        <td className="file-size">{formatFileSize(file.fileSize)}</td>
                        <td className="uploaded-by">{file.uploadedBy}</td>
                        <td className="upload-date">{formatDate(file.uploadedAt)}</td>
                        <td className="actions">
                          <button
                            type="button"
                            className="action-btn"
                            onClick={() => viewFile(file)}
                            title="View file content"
                          >
                            <span>👁️</span>
                          </button>
                          <button
                            className="action-btn download-btn"
                            onClick={() => downloadFile(file.id, file.originalName)}
                            title="Download file"
                          >
                            <span>📥</span>
                          </button>
                          <button
                            className="action-btn delete-btn"
                            onClick={() => deleteFile(file.id)}
                            title="Delete file"
                          >
                            <span>🗑️</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        {viewingFile && (
          <div className="modal-overlay" onClick={() => setViewingFile(null)}>
            <div className="modal-content" style={{ maxWidth: 900, width: "95%", maxHeight: "85vh", overflow: "hidden" }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Preview: {viewingFile.originalName}</h3>
                <button className="close-btn" onClick={() => setViewingFile(null)}>×</button>
              </div>
              <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                {viewFileLoading ? (
                  <p>Loading preview...</p>
                ) : viewFileError ? (
                  <p style={{ color: "#b91c1c" }}>{viewFileError}</p>
                ) : (
                  <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 13, lineHeight: 1.5 }}>
                    {viewFileContent || "(Empty)"}
                  </pre>
                )}
              </div>
            </div>
          </div>
        )}
        </>
      );
    };

    // Fetch organization info (including bot_quota)
    const fetchOrganizationInfo = useCallback(async () => {
      try {
        const response = await authenticatedFetch(`/api/organizations`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.organization) {
            setOrganizationInfo(result.organization);
          }
        }
      } catch (error) {
        console.error('Error fetching organization info:', error);
      }
    }, []);

    // Bot Management functions
    // fetchBots is defined earlier in the component (before useEffect hooks)

    const createBot = async (botData) => {
      try {
        const response = await authenticatedFetch(`/api/bots`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(botData),
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            await fetchBots();
            await fetchOrganizationInfo(); // Refresh organization info to update quota status
            setShowBotForm(false);
            setEditingBot(null);
            alert('Bot created successfully!');
          } else {
            alert(result.message || 'Failed to create bot');
          }
        } else {
          const error = await response.json();
          alert(error.message || 'Failed to create bot');
        }
      } catch (error) {
        console.error('Error creating bot:', error);
        alert('Error creating bot. Please try again.');
      }
    };

    const updateBot = async (botId, botData) => {
      try {
        const response = await authenticatedFetch(`/api/bots/${botId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            bot_name: botData.bot_name,
            description: botData.description ?? '',
            is_enabled: botData.is_enabled,
          }),
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            await fetchBots();
            setShowBotForm(false);
            setEditingBot(null);
            alert('Bot updated successfully!');
          } else {
            alert(result.message || 'Failed to update bot');
          }
        } else {
          const error = await response.json();
          alert(error.message || 'Failed to update bot');
        }
      } catch (error) {
        console.error('Error updating bot:', error);
        alert('Error updating bot. Please try again.');
      }
    };

    const deleteBot = async (botId) => {
      if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE bot "${botId}"?\n\nThis will delete:\n- The bot itself\n- All bot settings\n- All conversations\n- All related questions and tickets\n\nThis action CANNOT be undone!`)) {
        return;
      }

      try {
        const response = await authenticatedFetch(`/api/bots/${botId}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          const result = await response.json();
          await fetchBots();
          await fetchOrganizationInfo(); // Refresh organization info to update quota status
          alert(`Bot "${botId}" has been permanently deleted successfully!`);
        } else {
          const error = await response.json();
          alert(error.message || error.error || 'Failed to delete bot');
        }
      } catch (error) {
        console.error('Error deleting bot:', error);
        alert('Error deleting bot. Please try again.');
      }
    };

    const toggleBot = async (botId) => {
      try {
        const response = await authenticatedFetch(`/api/bots/${botId}/toggle`, {
          method: 'POST',
        });

        if (response.ok) {
          const result = await response.json();
          await fetchBots();
          alert(result.message || 'Bot status updated successfully!');
        } else {
          const error = await response.json();
          alert(error.message || 'Failed to toggle bot status');
        }
      } catch (error) {
        console.error('Error toggling bot:', error);
        alert('Error toggling bot status. Please try again.');
      }
    };

    // Load data when tab changes (dashboard stats are loaded by the dashboard-tab effect to avoid duplicate fetches)
    useEffect(() => {
      switch (activeTab) {
        case 'conversations':
          fetchConversations();
          break;
        case 'predefined':
          fetchPredefinedQuestions();
          break;
        case 'settings':
          fetchBotSettings();
          if (bots.length === 0) {
            fetchBots();
          }
          break;
        case 'bots':
          fetchBots();
          fetchOrganizationInfo();
          break;
        case 'redirect':
          fetchRedirectQuestions();
          break;
        case 'escalated':
          fetchEscalatedTickets();
          break;
        default:
          break;
      }
    }, [activeTab, fetchConversations, fetchPredefinedQuestions, fetchBotSettings, fetchBots, fetchOrganizationInfo, fetchRedirectQuestions, fetchEscalatedTickets, bots.length]);

    const renderBots = () => {
      // Calculate if bot quota is reached based on super admin's organization bot quota setting
      const botQuota = organizationInfo?.bot_quota ?? 0;
      const currentBotCount = bots.length;
      
      // Quota is reached if:
      // 1. bot_quota > 0 (quota is set, not unlimited)
      // 2. currentBotCount >= botQuota (already at or over the limit)
      const isQuotaReached = botQuota > 0 && currentBotCount >= botQuota;
      const canCreateBot = !isQuotaReached;
      
      // Debug logging
      console.log('[Bot Quota Check]', {
        botQuota,
        currentBotCount,
        isQuotaReached,
        canCreateBot,
        organizationInfo: organizationInfo ? { id: organizationInfo.id, name: organizationInfo.name, bot_quota: organizationInfo.bot_quota } : null
      });

      return (
        <div className="admin-section">
          <div className="section-header">
            <h2>Bot Management</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              {botQuota > 0 && (
                <span style={{ 
                  fontSize: '14px', 
                  color: isQuotaReached ? '#ef4444' : '#6b7280',
                  fontWeight: '500'
                }}>
                  Bots: {currentBotCount} / {botQuota}
                </span>
              )}
              <button
                className="btn-primary"
                disabled={!canCreateBot}
                onClick={(e) => {
                  e.preventDefault();
                  if (!canCreateBot) {
                    alert(`Bot quota limit reached.\n\nYour organization's bot quota is set to ${botQuota} bot(s) by the super admin.\nYou currently have ${currentBotCount} bot(s).\n\nPlease delete an existing bot or contact your administrator to increase the limit.`);
                    return;
                  }
                  setEditingBot(null);
                  setShowBotForm(true);
                }}
                style={{
                  opacity: canCreateBot ? 1 : 0.5,
                  cursor: canCreateBot ? 'pointer' : 'not-allowed',
                  position: 'relative',
                  backgroundColor: canCreateBot ? undefined : '#9ca3af',
                  borderColor: canCreateBot ? undefined : '#9ca3af'
                }}
                title={isQuotaReached ? `Bot quota limit reached (${currentBotCount}/${botQuota}). The super admin has set your organization's bot quota to ${botQuota}. Please delete an existing bot or contact your administrator to increase the limit.` : `Create a new bot. Current: ${currentBotCount}${botQuota > 0 ? ` / ${botQuota}` : ''}`}
              >
                + Create New Bot
              </button>
            </div>
          </div>
          
          {isQuotaReached && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              marginBottom: '20px',
              color: '#991b1b',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>Warning</span>
              <span>
                <strong>Bot quota limit reached!</strong> The super admin has set your organization's bot quota to <strong>{botQuota}</strong> bot(s). 
                You currently have <strong>{currentBotCount}</strong> bot(s). 
                Please delete an existing bot or contact your administrator to increase the limit.
              </span>
            </div>
          )}

          {showBotForm && (canCreateBot || editingBot) && (
            <BotForm
              bot={editingBot}
              onSubmit={(botData) => {
                // Double-check quota before submitting (safety check)
                const currentQuota = organizationInfo?.bot_quota ?? 0;
                const currentCount = bots.length;
                if (currentQuota > 0 && currentCount >= currentQuota) {
                  alert(`Bot quota limit reached! You have ${currentCount} bot(s) and your quota is ${currentQuota}. Please delete an existing bot first.`);
                  setShowBotForm(false);
                  return;
                }
                
                if (editingBot) {
                  updateBot(editingBot.bot_id, botData);
                } else {
                  createBot(botData);
                }
              }}
              onCancel={() => {
                setShowBotForm(false);
                setEditingBot(null);
              }}
            />
          )}

          <div className="bots-list">
            {isLoadingBots ? (
              <div className="loading">Loading bots...</div>
            ) : bots.length === 0 ? (
              <div className="empty-state">
                <p>No bots found. Create your first bot to get started!</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Bot ID</th>
                    <th>Bot Name</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bots.map((bot) => (
                    <tr key={bot.id}>
                      <td><code>{bot.bot_id}</code></td>
                      <td>{bot.bot_name}</td>
                      <td>{bot.description || '-'}</td>
                      <td>
                        <span className={`status-badge ${bot.is_enabled ? 'active' : 'inactive'}`}>
                          {bot.is_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td>{formatDate(bot.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-small btn-secondary"
                            onClick={() => toggleBot(bot.bot_id)}
                            title={bot.is_enabled ? 'Disable bot' : 'Enable bot'}
                          >
                            {bot.is_enabled ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            className="btn-small btn-primary"
                            onClick={() => {
                              setEditingBot(bot);
                              setShowBotForm(true);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-small btn-danger"
                            onClick={() => deleteBot(bot.bot_id)}
                          >
                            Delete
                          </button>
                          <button
                            className="btn-small btn-info"
                            onClick={() => {
                              setSelectedBotId(bot.bot_id);
                              setActiveTab('settings');
                            }}
                          >
                            Settings
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      );
    };

    const handleSaveRedirectQuestion = async (data) => {
      try {
        const popupKeywords = parseKeywordList(botSettings?.popup_trigger_words);
        const overlapsWithPopup = findKeywordOverlaps(data.keywords || [], popupKeywords);
        if (overlapsWithPopup.length) {
          alert(formatKeywordConflictMessage(overlapsWithPopup, 'Popup Configuration'));
          return;
        }

        const url = editingRedirectQuestion
          ? `/api/redirect-questions/${editingRedirectQuestion.id}`
          : `/api/redirect-questions`;

        const method = editingRedirectQuestion ? 'PUT' : 'POST';
        const body = { ...data, bot_id: selectedBotId };

        console.log('[AdminPanel] Saving redirect question:', { url, method, body });

        const response = await authenticatedFetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });

        if (response.ok) {
          const result = await response.json();
          console.log('[AdminPanel] Redirect question saved successfully:', result);
          alert(`Redirect question ${editingRedirectQuestion ? 'updated' : 'created'} successfully!`);
          setShowRedirectForm(false);
          setEditingRedirectQuestion(null);
          // Refresh the list
          await fetchRedirectQuestions();
        } else {
          const error = await response.json();
          console.error('[AdminPanel] Failed to save redirect question:', response.status, error);
          alert(`Failed to ${editingRedirectQuestion ? 'update' : 'create'} redirect question: ${error.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error saving redirect question:', error);
        alert('Failed to save redirect question. Please try again.');
      }
    };

    const handleDeleteRedirectQuestion = async (id) => {
      if (!window.confirm('Are you sure you want to delete this redirect trigger?')) return;
      try {
        const response = await authenticatedFetch(`/api/redirect-questions/${id}`, { method: 'DELETE' });
        if (response.ok) {
          fetchRedirectQuestions();
        }
      } catch (error) {
        console.error('Error deleting redirect question:', error);
      }
    };

    const renderRedirectQuestions = () => {
      return (
        <div className="predefined-content">
          <div className="predefined-header">
            <div className="header-main">
              <h2>Redirect Triggers</h2>
              <p className="header-subtitle">Manage topics that should trigger an immediate human escalation instead of an AI response.</p>
            </div>
            <button className="add-question-btn" onClick={() => { setEditingRedirectQuestion(null); setShowRedirectForm(true); }}>
              Add Trigger
            </button>
          </div>

          {showRedirectForm && (
            <RedirectForm
              question={editingRedirectQuestion}
              onSubmit={handleSaveRedirectQuestion}
              onCancel={() => { setShowRedirectForm(false); setEditingRedirectQuestion(null); }}
            />
          )}

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Sample Question</th>
                  <th>Keywords</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {redirectQuestions.length === 0 ? (
                  <tr><td colSpan="4" className="empty-row">No redirect triggers configured.</td></tr>
                ) : (
                  redirectQuestions.map(q => (
                    <tr key={q.id}>
                      <td><strong>{q.question_text}</strong></td>
                      <td>
                        <div className="keyword-tags">
                          {(q.keywords || []).map((kw, i) => <span key={i} className="keyword-tag">{kw}</span>)}
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${q.is_active ? 'active' : 'inactive'}`}>
                          {q.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="table-actions">
                        <div className="action-buttons">
                          <button className="btn-small btn-primary" onClick={() => { setEditingRedirectQuestion(q); setShowRedirectForm(true); }}>Edit</button>
                          <button className="btn-small btn-danger" onClick={() => handleDeleteRedirectQuestion(q.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    };

    const renderEscalatedTickets = () => {
      return (
        <div className="predefined-content">
          <div className="predefined-header">
            <div className="header-main">
              <h2>Escalated Tickets</h2>
              <p className="header-subtitle">Urgent requests flagged by the bot's automated security detection.</p>
            </div>
            <button className="refresh-stats-btn" onClick={fetchEscalatedTickets}>Refresh List</button>
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Requester</th>
                  <th>Contact Info</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {escalatedTickets.length === 0 ? (
                  <tr><td colSpan="6" className="empty-row">No escalated tickets found.</td></tr>
                ) : (
                  escalatedTickets.map(t => (
                    <tr key={t.id} className="escalated-row">
                      <td><div style={{ fontWeight: '600', color: '#ffffff' }}>{t.user_name}</div></td>
                      <td>
                        <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                          <span>Email:</span> {t.user_email}
                        </div>
                        <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span>Phone:</span> {t.user_phone || 'N/A'}
                        </div>
                      </td>
                      <td><span className="priority-high-badge">HIGH</span></td>
                      <td>
                        <select
                          className={`status-select ${t.status}`}
                          value={t.status || ''}
                          onChange={(e) => updateTicketStatus(t.id, e.target.value)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                            border: '1px solid #e2e8f0',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="open">OPEN</option>
                          <option value="pending">PENDING</option>
                          <option value="active">ACTIVE</option>
                          <option value="in-progress">IN PROGRESS</option>
                          <option value="resolved">RESOLVED</option>
                          <option value="closed">CLOSED</option>
                        </select>
                      </td>
                      <td style={{ fontSize: '12px', color: '#64748b' }}>{formatDate(t.created_at)}</td>
                      <td className="table-actions">
                        <button
                          className="btn-small btn-info"
                          style={{ border: '1px solid #0ea5e9' }}
                          onClick={() => {
                            setActiveTab('conversations');
                            fetchConversationDetails(t.conversation_id);
                          }}
                        >
                          View Chat
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    };

    // Bot Form Component
    const BotForm = ({ bot, onSubmit, onCancel }) => {
      const [formData, setFormData] = useState({
        bot_id: bot?.bot_id || '',
        bot_name: bot?.bot_name || '',
        description: bot?.description || '',
        is_enabled: bot?.is_enabled !== undefined ? bot.is_enabled : true,
        org_id: bot?.org_id || ''
      });

      const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.bot_id.trim() || !formData.bot_name.trim()) {
          alert('Bot ID and Bot Name are required');
          return;
        }
        onSubmit(formData);
      };

      return (
        <div className="form-overlay">
          <div className="form-modal">
            <h3>{bot ? 'Edit Bot' : 'Create New Bot'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Bot ID *</label>
                <input
                  type="text"
                  value={formData.bot_id || ''}
                  onChange={(e) => setFormData({ ...formData, bot_id: e.target.value })}
                  placeholder="e.g., bot-123, support-bot"
                  required
                  disabled={!!bot} // Can't change bot_id after creation
                  pattern="[a-zA-Z0-9_\-]+"
                  title="Only letters, numbers, dashes, and underscores allowed"
                />
                <small>Unique identifier for this bot (cannot be changed after creation)</small>
              </div>

              <div className="form-group">
                <label>Bot Name *</label>
                <input
                  type="text"
                  value={formData.bot_name || ''}
                  onChange={(e) => setFormData({ ...formData, bot_name: e.target.value })}
                  placeholder="e.g., Customer Support Bot"
                  required
                />
              </div>

              <div className="form-group">
                <label>Organization</label>
                <select
                  value={formData.org_id || ''}
                  onChange={(e) => setFormData({ ...formData, org_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <option value="">None (System Bot)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description for this bot"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_enabled}
                    onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
                  />
                  Enabled
                </label>
                <small>Disabled bots cannot be used in widgets or chat</small>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {bot ? 'Update Bot' : 'Create Bot'}
                </button>
                <button type="button" className="btn-secondary" onClick={onCancel}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      );
    };

    const renderContent = () => {
      switch (activeTab) {
        case "dashboard":
          return renderDashboard();
        case "conversations":
          return renderConversations();
        case "predefined":
          return renderPredefinedQuestions();
        case "repetitive":
          return renderRepetitiveQuestions();
        case "settings":
          return renderSettings();
        case "uploads":
          return renderUploads();
        case "bots":
          return renderBots();
        case "redirect":
          return renderRedirectQuestions();
        case 'escalated':
          return renderEscalatedTickets();
        default:
          return renderDashboard();
      }
    };

    return (
      <div className="admin-panel">
        <header className="admin-header">
          <div className="header-left">
            <div className="admin-brand">
              <img className="admin-brand-logo" src={`${process.env.PUBLIC_URL || ''}/assets/wetano-logo.png`} alt="WeTano Solutions" />
              <span className="admin-brand-name">WeTano Solutions</span>
            </div>
            <h1>Admin Console</h1>
          </div>
          <div className="header-right">
            <span className="welcome-text">Welcome, {userInfo.username}!</span>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </header>

        <div className="admin-content">
          <nav className="admin-sidebar">
            <ul className="nav-list">
              <li
                className={`nav-item ${activeTab === "dashboard" ? "active" : ""}`}
                onClick={() => setActiveTab("dashboard")}
              >
                <span className="nav-icon">DB</span>
                <span className="nav-label">Dashboard</span>
              </li>
              <li
                className={`nav-item ${activeTab === "conversations" ? "active" : ""}`}
                onClick={() => setActiveTab("conversations")}
              >
                <span className="nav-icon">TK</span>
                <span className="nav-label">Tickets</span>
              </li>
              <li
                className={`nav-item ${activeTab === "settings" ? "active" : ""}`}
                onClick={() => setActiveTab("settings")}
              >
                <span className="nav-icon">ST</span>
                <span className="nav-label">Bot Settings</span>
              </li>
              <li
                className={`nav-item ${activeTab === "uploads" ? "active" : ""}`}
                onClick={() => setActiveTab("uploads")}
              >
                <span className="nav-icon">KB</span>
                <span className="nav-label">Knowledge Base</span>
              </li>
              <li
                className={`nav-item ${activeTab === "predefined" ? "active" : ""}`}
                onClick={() => setActiveTab("predefined")}
              >
                <span className="nav-icon">PQ</span>
                <span className="nav-label">Predefined Qs</span>
              </li>
              <li
                className={`nav-item ${activeTab === "repetitive" ? "active" : ""}`}
                onClick={() => setActiveTab("repetitive")}
              >
                <span className="nav-icon">SQ</span>
                <span className="nav-label">Suggested Qs</span>
              </li>
              <li
                className={`nav-item ${activeTab === "bots" ? "active" : ""}`}
                onClick={() => setActiveTab("bots")}
              >
                <span className="nav-icon">BT</span>
                <span className="nav-label">Bots</span>
              </li>
              <li
                className={`nav-item ${activeTab === "redirect" ? "active" : ""}`}
                onClick={() => setActiveTab("redirect")}
              >
                <span className="nav-icon">RT</span>
                <span className="nav-label">Redirect Qs</span>
              </li>
              <li
                className={`nav-item ${activeTab === "escalated" ? "active" : ""}`}
                onClick={() => setActiveTab("escalated")}
              >
                <span className="nav-icon">ET</span>
                <span className="nav-label">Escalated Qs</span>
              </li>
            </ul>
          </nav>

          <main className="admin-main">
            {renderContent()}
          </main>
        </div>
      </div>
    );
  };

  export default AdminPanel;
